import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  PackagePlus, Plus, X, Search, Trash2, Edit2, RefreshCw, Eye, FileText,
  Ban, ChevronDown, Calendar, Banknote, ClipboardList, Truck, ListChecks,
  Pill, Box, ChevronRight, AlertTriangle, Paperclip
} from 'lucide-react';
import { stockReceiptAPI, vendorAPI, medicineAPI, clinicalSettingsAPI, paymentProofAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import { authedMediaUrl } from '../utils/media';
import { FileHoverPreview } from './MediaPreview';
import PaymentProofUpload from './PaymentProofUpload';
import { PAYMENT_MODES, PAYMENT_MODE_LABEL, withRetiredMode, paymentNeedsProof } from '../constants/payments';
import DatePicker from './DatePicker';
import RowActionsMenu from './RowActionsMenu';
import SortHeader, { useSort, sortRows } from './SortHeader';
import TablePager, { usePagedRows } from './TablePager';
import { MedicineFormModal, REPACKAGEABLE_TYPES, SOLID_PACK_TYPES, solidPackNoun, solidUnitNoun } from './MedicinesManagement';
import Select from './Select';
import { formatClinicDate, formatClinicDateTime } from '../utils/clinicTime';
import { packFactorFor, rowFactorFor } from '../utils/packMath';
import useDialog, { markDialogDirty } from '../hooks/useDialog';
import { onActivateKey, ROW_FOCUS_RING } from '../utils/keyboard';

const formatCurrency = (n) => `Rs. ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Group an in-progress amount with thousands separators while preserving the decimal the user is
// typing (e.g. "9330" → "9,330", "1234.5" → "1,234.5"). Edit-safe for a controlled text input.
const formatAmountInput = (raw) => {
  if (raw === '' || raw == null) return '';
  const [intPart, decPart] = String(raw).split('.');
  const grouped = (intPart || '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
};
// Keep only digits and a single decimal point (what we store as the raw amount).
const parseAmountInput = (v) => {
  const cleaned = String(v).replace(/[^0-9.]/g, '');
  const i = cleaned.indexOf('.');
  return i === -1 ? cleaned : cleaned.slice(0, i + 1) + cleaned.slice(i + 1).replace(/\./g, '');
};
// Render a date in the clinic's timezone (tz-safe — see clinicTime util).
const formatDate = (d) => formatClinicDate(d, { day: '2-digit', month: 'short', year: 'numeric' }, '-');
// Percentages come back as BigDecimal, so 5 arrives as "5.00". Trim the noise but keep real
// precision: 5.00 -> 5, 12.50 -> 12.5, 17.25 -> 17.25.
const formatPercent = (v) =>
  `${Number(v).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')}%`;
const todayIso = () => new Date().toISOString().split('T')[0];

// Session key Medicine Orders leaves an order under when its Receive is pressed: {orderId, vendorId}.
export const RECEIVE_ORDER_HANDOFF = 'pamh:receive-order';

// Payment methods now live in one shared module (src/constants/payments.js) rather than being
// declared here and imported by everything else. Re-exported so existing importers keep working.
export { PAYMENT_MODES, PAYMENT_MODE_LABEL };

// Payment Mode select + Instrument/Reference input, shared by the Medicine and Asset receipt
// forms (and the Assets module). The reference field only shows for non-cash modes (cash has
// no instrument).
export const PaymentFields = ({ paymentMode, setPaymentMode, paymentReference, setPaymentReference, allowUnset = false }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <div>
      <label className="block text-sm font-semibold mb-1">Payment Mode</label>
      <Select
        value={paymentMode}
        onChange={setPaymentMode}
        // withRetiredMode: a record saved under Online / Credit / Other keeps showing it, marked as
        // no longer offered, instead of being silently rewritten to whatever is first in the list.
        options={withRetiredMode(
          [...(allowUnset ? [{ value: '', label: 'Not recorded' }] : []), ...PAYMENT_MODES],
          paymentMode,
        )}
        buttonClassName="p-2.5 rounded-lg"
      />
    </div>
    {paymentMode && paymentMode !== 'CASH' && (
      <div className="md:col-span-2">
        <label className="block text-sm font-semibold mb-1">Instrument / Reference</label>
        <input
          type="text"
          value={paymentReference}
          onChange={e => setPaymentReference(e.target.value)}
          maxLength={500}
          placeholder="e.g. Cheque # & bank, transaction ID, terms"
          className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>
    )}
  </div>
);

// Status labels — the backend stores ACTIVE / CANCELLED but "Active" reads oddly for
// a stock receipt (it sounds like an enable/disable toggle). A saved receipt is really
// "Completed" — the goods were received and stock was added. Renaming display-side only
// keeps the data layer stable.
const STATUS_STYLE = {
  ACTIVE:    { label: 'Completed', color: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  CANCELLED: { label: 'Cancelled', color: 'bg-rose-100 text-rose-700',       dot: 'bg-rose-500' },
};

// Human label for a medicine type enum (TABLET → "Tablet", EYE_DROPS → "Eye Drops").
const typeLabel = (t) =>
  t ? String(t).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';

const StockReceiptsManagement = ({ hasPermission }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('PHARMACY_STOCK_RECEIPTS', action) : true),
    [hasPermission],
  );
  // Anyone who can record a stock receipt can register a not-yet-listed medicine inline — it's
  // part of receiving new stock, and reaching the receipt form already implies that permission.
  // (Deliberately NOT gated by the separate Medicines "create" permission, which view-only roles
  // like Doctor don't have — that would hide the affordance from the very people recording stock.)
  const canCreateMedicine = can('CREATE') || can('EDIT');

  const [receipts, setReceipts] = useState([]);
  // Medicine or asset receipts only. Filtered here rather than by the server: the list already
  // carries every receipt's type, and the vendor and dates are what narrow the fetch.
  const [filterType, setFilterType] = useState('');
  const typedReceipts = useMemo(
    () => (filterType ? receipts.filter((r) => (r.receiptType || 'MEDICINE') === filterType) : receipts),
    [receipts, filterType],
  );
  // Newest posted first (owner, 2026-10-30): a bill dated days ago still lands at the top on the
  // day it is entered, where the person who just entered it looks for it.
  // Total sorts on its number rather than on the formatted cell, and Type/Payment/Status on the
  // label the chip shows so the order matches what is on screen.
  const { sortConfig, handleSort } = useSort('postedAt', 'desc');
  const sortedReceipts = useMemo(() => sortRows(typedReceipts, sortConfig, {
    invoiceNumber: (r) => r.invoiceNumber,
    invoiceDate:   (r) => r.invoiceDate,
    postedAt:      (r) => r.createdAt,
    type:          (r) => ((r.receiptType || 'MEDICINE') === 'ASSET' ? 'Asset' : 'Medicine'),
    vendor:        (r) => r.vendorName,
    total:         (r) => Number(r.totalAmount || 0),
    payment:       (r) => (r.paymentMode ? (PAYMENT_MODE_LABEL[r.paymentMode] || r.paymentMode) : ''),
    status:        (r) => (STATUS_STYLE[r.status] || STATUS_STYLE.ACTIVE).label,
  }), [typedReceipts, sortConfig]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [medicines, setMedicines] = useState([]);

  const [filterVendor, setFilterVendor] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const paging = usePagedRows(sortedReceipts, {
    id: 'stock-receipts',
    resetOn: [sortConfig, filterVendor, filterType, filterFrom, filterTo],
  });

  const [editing, setEditing] = useState(null);          // receipt being edited (null when not editing)
  // Receipt-creation flow: null = no modal open, 'picker' = type chooser, 'medicine' or
  // 'asset' = the corresponding form modal. New Receipt opens 'picker' first; the picker
  // dispatches to the right form depending on what the admin chose.
  const [creating, setCreating] = useState(null);
  // Split "New Receipt" button: the main action opens a Medicine receipt directly (the common
  // case); the caret opens a small menu to pick Medicine or Assets & Equipment.
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const newMenuRef = useRef(null);
  useEffect(() => {
    if (!newMenuOpen) return;
    const onDown = (e) => { if (!newMenuRef.current?.contains(e.target)) setNewMenuOpen(false); };
    // Escape closes the menu and returns focus to the caret that opened it.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setNewMenuOpen(false);
      newMenuRef.current?.querySelector('button[title="Choose receipt type"]')?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [newMenuOpen]);
  const [viewing, setViewing] = useState(null);          // receipt being viewed (read-only)

  // "Receive" on Medicine Orders hands the order over to here, since a delivery is received as a
  // receipt and nowhere else. Read once, then removed, so a later visit opens the page as usual.
  const [orderPrefill, setOrderPrefill] = useState(null);
  useEffect(() => {
    let handoff = null;
    try {
      handoff = JSON.parse(sessionStorage.getItem(RECEIVE_ORDER_HANDOFF) || 'null');
      sessionStorage.removeItem(RECEIVE_ORDER_HANDOFF);
    } catch { handoff = null; }
    if (handoff?.orderId && handoff?.vendorId) {
      setOrderPrefill(handoff);
      setCreating('medicine');
    }
  }, []);

  const loadReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await stockReceiptAPI.list({
        vendorId: filterVendor || undefined,
        from: filterFrom || undefined,
        to: filterTo || undefined,
      });
      setReceipts(data || []);
    } catch (e) {
      notify('error', 'Failed to load stock receipts');
    } finally {
      setLoading(false);
    }
  }, [filterVendor, filterFrom, filterTo, notify]);

  useEffect(() => { loadReceipts(); }, [loadReceipts]);

  // Vendors + medicines are needed by the form modal. Load once on mount and reuse.
  useEffect(() => {
    (async () => {
      try {
        const [vs, ms] = await Promise.all([vendorAPI.getAll(), medicineAPI.getAll()]);
        // Filter to active vendors only — we don't want to record new deliveries against
        // suspended/inactive vendors.
        setVendors((vs || []).filter(v => v.status === 'ACTIVE' || !v.status));
        setMedicines(ms || []);
      } catch (e) {
        notify('error', 'Failed to load vendors/medicines');
      }
    })();
  }, [notify]);

  const handleCancel = async (receipt) => {
    const isAsset = (receipt.receiptType || 'MEDICINE') === 'ASSET';
    const ok = await confirm({
      title: 'Cancel this stock receipt?',
      message: isAsset
        ? `Receipt ${receipt.invoiceNumber} from ${receipt.vendorName} will be marked Cancelled. The Asset records that were auto-created from this receipt will be LEFT IN PLACE - admins can dispose them individually from the Assets page if needed.`
        : `Receipt ${receipt.invoiceNumber} from ${receipt.vendorName} will be reversed - quantities will be deducted from the affected medicines. Continue?`,
      confirmText: 'Cancel Receipt',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await stockReceiptAPI.cancel(receipt.id);
      notify('success', isAsset ? 'Receipt cancelled' : 'Receipt cancelled - inventory adjusted');
      loadReceipts();
    } catch (e) {
      notify('error', e.message || 'Failed to cancel receipt');
    }
  };

  const handleOpenEdit = async (receipt) => {
    try {
      const full = await stockReceiptAPI.getById(receipt.id);
      setEditing(full);
    } catch (e) {
      notify('error', 'Failed to load receipt');
    }
  };

  // Cross-page handoff from Medicines → Update Stock. The Medicines modal stores the chosen
  // receipt id under this key and navigates here; we consume it on mount and auto-open the
  // edit modal (falling back to a toast if the receipt has since been deleted).
  useEffect(() => {
    const pendingId = sessionStorage.getItem('openStockReceiptId');
    if (!pendingId) return;
    sessionStorage.removeItem('openStockReceiptId');
    (async () => {
      try {
        const full = await stockReceiptAPI.getById(pendingId);
        setEditing(full);
      } catch (e) {
        notify('error', 'Could not open the requested receipt');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center">
              <PackagePlus className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Stock Receipts</h1>
              <p className="text-gray-600">Record deliveries from vendors and update inventory</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex" ref={newMenuRef}>
              <button
                onClick={() => { setNewMenuOpen(false); setCreating('medicine'); }}
                className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-l-lg hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4" />
                <span>New Receipt</span>
              </button>
              <button
                onClick={() => setNewMenuOpen(o => !o)}
                className="px-2 bg-emerald-600 text-white rounded-r-lg hover:bg-emerald-700 border-l border-emerald-500"
                title="Choose receipt type"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              {newMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-20">
                  <button
                    onClick={() => { setNewMenuOpen(false); setCreating('medicine'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-emerald-50"
                  >
                    <Pill className="w-4 h-4 text-emerald-600" /> Medicine
                  </button>
                  <button
                    onClick={() => { setNewMenuOpen(false); setCreating('asset'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-emerald-50"
                  >
                    <Box className="w-4 h-4 text-blue-600" /> Assets &amp; Equipment
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={loadReceipts}
              className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Vendor</label>
            <Select
              value={filterVendor}
              onChange={setFilterVendor}
              options={[{ value: '', label: 'All vendors' }, ...vendors.map(v => ({ value: v.id, label: v.vendorName }))]}
              buttonClassName="px-3 py-2 rounded-lg"
            />
          </div>
          <div>
            <label htmlFor="receipt-type-filter" className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
            <Select
              id="receipt-type-filter"
              value={filterType}
              onChange={setFilterType}
              options={[
                { value: '', label: 'All types' },
                { value: 'ASSET', label: 'Asset' },
                { value: 'MEDICINE', label: 'Medicine' },
              ]}
              buttonClassName="px-3 py-2 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
            <DatePicker value={filterFrom} onChange={setFilterFrom} placeholder="Start date" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
            <DatePicker value={filterTo} onChange={setFilterTo} placeholder="End date" />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => { setFilterVendor(''); setFilterType(''); setFilterFrom(''); setFilterTo(''); }}
              className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full"
            >
              Clear filters
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {!loading && <TablePager paging={paging} noun="receipt" />}
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
                <SortHeader label="Invoice #" sortKey="invoiceNumber" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <SortHeader label="Invoice Date" sortKey="invoiceDate" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Posted Date" sortKey="postedAt" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Type" sortKey="type" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Vendor" sortKey="vendor" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Total" sortKey="total" sortConfig={sortConfig} onSort={handleSort} align="right" />
                <SortHeader label="Payment" sortKey="payment" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} align="center" />
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="9" className="py-12 text-center text-gray-500">Loading…</td></tr>
              ) : receipts.length === 0 ? (
                <tr><td colSpan="9" className="py-12 text-center text-gray-500">
                  No stock receipts yet. Click "New Receipt" to record a delivery.
                </td></tr>
              ) : typedReceipts.length === 0 ? (
                <tr><td colSpan="9" className="py-12 text-center text-gray-500">
                  No {filterType === 'ASSET' ? 'asset' : 'medicine'} receipts for these filters.
                </td></tr>
              ) : paging.pageRows.map(r => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.ACTIVE;
                return (
                  <tr
                    key={r.id}
                    tabIndex={0}
                    onClick={async () => { setViewing(await stockReceiptAPI.getById(r.id)); }}
                    onKeyDown={onActivateKey(async () => { setViewing(await stockReceiptAPI.getById(r.id)); })}
                    className={`hover:bg-gray-50/50 cursor-pointer ${ROW_FOCUS_RING}`}
                  >
                    <td className="px-6 py-3 font-mono text-sm">{r.invoiceNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(r.invoiceDate)}</td>
                    {/* When the receipt was entered into the system, which can be days after the
                        supplier dated the bill. The hover gives the time as well. */}
                    <td className="px-4 py-3 text-sm text-gray-600" title={formatClinicDateTime(r.createdAt, {}, '')}>
                      {formatDate(r.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      {/* Type chip — emerald for medicine, blue for asset. Mirrors the
                          colours used on the type-picker modal. */}
                      {(r.receiptType || 'MEDICINE') === 'ASSET' ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          <Box className="h-3 w-3" /> Asset
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <Pill className="h-3 w-3" /> Medicine
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{r.vendorName}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">{formatCurrency(r.totalAmount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {r.paymentMode ? (
                        <span title={r.paymentReference || ''}>
                          {PAYMENT_MODE_LABEL[r.paymentMode] || r.paymentMode}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${st.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${st.dot}`}></span>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center">
                        <RowActionsMenu items={[
                          { key: 'view', label: 'View',   icon: Eye,    iconClass: 'text-blue-500',
                            onClick: async () => { setViewing(await stockReceiptAPI.getById(r.id)); } },
                          { key: 'edit', label: 'Edit',   icon: Edit2,  iconClass: 'text-emerald-500',
                            // Permission-gated; allowed any time. A medicine receipt re-applies its
                            // stock on save; an asset receipt carries its changes onto the assets it
                            // created (StockReceiptService.updateAssetReceipt).
                            hidden: r.status !== 'ACTIVE' || !can('EDIT'),
                            onClick: () => handleOpenEdit(r) },
                          { key: 'cancel', label: 'Cancel receipt', icon: Ban, danger: true,
                            subtitle: (r.receiptType || 'MEDICINE') === 'ASSET'
                              ? 'Created assets left in place'
                              : 'Reverses inventory',
                            hidden: r.status !== 'ACTIVE' || !can('CANCEL_RECEIPT'),
                            onClick: () => handleCancel(r) },
                        ]} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* The count repeated where a scrolled reader ends up, with the page buttons. */}
          {!loading && <TablePager paging={paging} noun="receipt" position="bottom" />}
        </div>
      </div>

      {(creating === 'medicine' || (editing && (editing.receiptType || 'MEDICINE') === 'MEDICINE')) && (
        <ReceiptFormModal
          existing={editing}
          prefill={editing ? null : orderPrefill}
          // Medicine receipts only show Pharmaceutical + Laboratory vendors — those are
          // the only categories that supply medicines / lab reagents. Other vendor
          // types (IT, Furniture, Maintenance, etc.) go through the Asset receipt flow.
          vendors={vendors.filter(v => v.vendorType === 'PHARMACEUTICAL' || v.vendorType === 'LABORATORY')}
          medicines={medicines}
          canCreateMedicine={canCreateMedicine}
          onMedicineAdded={(med) => setMedicines(prev => [med, ...prev])}
          onMedicineUpdated={(med) => setMedicines(prev => prev.map(m => (m.id === med.id ? med : m)))}
          onClose={() => { setCreating(null); setEditing(null); setOrderPrefill(null); }}
          onSaved={() => {
            setCreating(null);
            setEditing(null);
            setOrderPrefill(null);
            loadReceipts();
          }}
        />
      )}

      {(creating === 'asset' || (editing && editing.receiptType === 'ASSET')) && (
        <AssetReceiptFormModal
          existing={creating === 'asset' ? null : editing}
          // Hide pharmaceutical vendors on asset receipts — they only supply medicines.
          // Everything else (Medical Equipment, IT, Furniture, Maintenance, Lab, etc.)
          // can plausibly supply physical assets so we keep those visible. A receipt being
          // edited keeps its own vendor whatever its type, or the vendor would show blank.
          vendors={vendors.filter(v => v.vendorType !== 'PHARMACEUTICAL'
            || (creating !== 'asset' && v.id === editing?.vendorId))}
          onClose={() => { setCreating(null); setEditing(null); }}
          onSaved={() => { setCreating(null); setEditing(null); loadReceipts(); }}
        />
      )}

      {viewing && (
        <ReceiptViewModal
          receipt={viewing}
          onClose={() => setViewing(null)}
          // The same conditions the row menu uses. Repeated rather than shared because the row has
          // a summary and this has the full record; keep them in step.
          canEdit={viewing.status === 'ACTIVE' && can('EDIT')}
          onEdit={(r) => { setViewing(null); setEditing(r); }}
        />
      )}
    </div>
  );
};

/** "Not against any order", said on purpose: the server then matches the receipt to no order. */
const NO_ORDER = 'none';

/**
 * The order a new receipt would be matched to: of the supplier's waiting orders, the one still
 * expecting the most of the items on the bill, the oldest where two expect as many (the list comes
 * oldest first). The server applies the same rule when the receipt is saved.
 */
export const matchOrder = (openOrders, medicineIds) => {
  const onBill = new Set((medicineIds || []).filter(Boolean));
  if (onBill.size === 0) return null;
  let best = null;
  let bestCount = 0;
  (openOrders || []).forEach(o => {
    const expecting = new Set((o.items || []).filter(i => i.remaining > 0 && onBill.has(i.medicineId)).map(i => i.medicineId)).size;
    if (expecting > bestCount) { best = o; bestCount = expecting; }
  });
  return best;
};

// =============================== FORM MODAL ===============================

// A supplier states discount and tax either as percentages or as rupee amounts, and the two are
// stated independently ("Discount Rs 500, Sales Tax 17%" is an ordinary bill). The choice belongs to
// the WHOLE receipt — one invoice does not mix units for the same charge — while the figure itself
// is recorded per line either way, because a supplier bills each item its own discount.
const CHARGE_MODES = { PERCENT: 'PERCENT', AMOUNT: 'AMOUNT' };

// What the distributor delivers a medicine IN, and therefore what the person entering the receipt
// counts off the bill. Tablets and capsules arrive in packs, syrups and suspensions in bottles, and
// consumables in packs. Everything else — injections, creams, drops — is received as loose units, so
// the column is hidden and Qty is typed straight in.
const PACK_ENTRY_LABEL = {
  TABLET: 'Packs', CAPSULE: 'Packs',
  SYRUP: 'Bottles', SUSPENSION: 'Bottles', SOLUTION: 'Bottles',
  SACHET: 'Packs',
  SYRINGE: 'Packs', GLOVES: 'Packs', TEST_STRIP: 'Packs', DRESSING: 'Packs',
  MASK: 'Packs', TUBING: 'Packs', SUTURE: 'Packs', CONSUMABLE: 'Packs',
};
const packEntryLabelFor = (type) => PACK_ENTRY_LABEL[type] || null;
// The same word in prose: "5 packs of 10", "1 bottle of 120".
const packEntryNoun = (type, count) => {
  const base = (PACK_ENTRY_LABEL[type] || 'Packs').toLowerCase().slice(0, -1);
  return count === 1 ? base : base + 's';
};

/** File size for the attachment list. Bytes are never the useful unit here. */
const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Whether this invoice states one of its charges as a percentage or as a rupee amount. Set once for
 * the whole receipt; the figures themselves are still typed per line.
 */
const ChargeModeToggle = ({ label, mode, onMode }) => (
  <div className="inline-flex items-center gap-1.5">
    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
      {[
        { key: CHARGE_MODES.PERCENT, text: '%' },
        { key: CHARGE_MODES.AMOUNT, text: 'Rs' },
      ].map(o => (
        <button
          key={o.key}
          type="button"
          onClick={() => onMode(o.key)}
          title={`Every line's ${label.toLowerCase()} on this receipt is ${
            o.key === CHARGE_MODES.PERCENT ? 'a percentage' : 'a rupee amount'}`}
          className={`px-2.5 py-1 text-xs font-semibold transition-colors ${
            mode === o.key ? 'bg-emerald-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          {o.text}
        </button>
      ))}
    </div>
  </div>
);

// Receipt line rows need an identity of their own. Keyed by position, removing a row handed its
// DOM, including the focused Remove button, to the row that slid into its place, so a second Enter
// removed that line too. _key never leaves the browser: the save maps each line field by field.
let lineKeySeq = 0;
const nextLineKey = () => `line-${++lineKeySeq}`;

const blankLine = () => ({
  _key: nextLineKey(),
  medicineId: '',
  medicineDisplay: '',
  batchNumber: '',
  expiryDate: '',
  // Qty is the number of packs received; the unit count is packs x the medicine's units-per-pack.
  quantityReceived: '',
  // The line's amount exactly as the bill prints it, before discount and tax.
  grossAmount: '',
  unitSellingPrice: '',
  // Repackaging capture. For a repackaged medicine these are the primary inputs and
  // quantityReceived / unitPurchasePrice are derived from them. unitsPerPackSnapshot is the
  // factor the receipt was saved with (edit mode) so the row's math never shifts under it.
  packsReceived: '',
  packPrice: '',
  unitsPerPackSnapshot: null,
  // The purchase-order line this delivery line fills, when the receipt is against an order.
  orderItemId: null,
  // Per-line trade discount and sales tax. Which pair is in use is decided once for the whole
  // receipt (CHARGE_MODES); only the matching pair is sent. Discount is applied first, then tax on
  // the remainder, and the effective unit cost falls out of the net.
  discountPercent: '',
  taxPercent: '',
  discountAmount: '',
  taxAmount: '',
  // True once the user types their own sell price, which stops the markup auto-suggestion from
  // overwriting it when the pack price changes.
  sellManuallySet: false,
});

// =========================== ASSET RECEIPT MODAL ===========================
// Asset categories — mirror the AssetCategory enum on the backend. Alphabetical by
// display label with OTHER pinned to the end (standard UX convention).
const ASSET_CATEGORY_OPTIONS = [
  { value: 'ELECTRICAL', label: 'Electrical' },
  { value: 'FURNITURE', label: 'Furniture' },
  { value: 'HVAC', label: 'HVAC' },
  { value: 'IMAGING_EQUIPMENT', label: 'Imaging Equipment' },
  { value: 'IT_EQUIPMENT', label: 'IT Equipment' },
  { value: 'LABORATORY_EQUIPMENT', label: 'Laboratory Equipment' },
  { value: 'MEDICAL_EQUIPMENT', label: 'Medical Equipment' },
  { value: 'PATIENT_CARE', label: 'Patient Care' },
  { value: 'SURGICAL_INSTRUMENTS', label: 'Surgical Instruments' },
  { value: 'VEHICLE', label: 'Vehicle' },
  { value: 'OTHER', label: 'Other' },
];

const emptyAssetLine = () => ({
  // The saved line this row is, when editing. Sent back so the line keeps its assets; a row added
  // in the edit has none and brings new assets.
  id: null,
  assetName: '',
  assetCategory: '',
  assetBrand: '',
  assetModel: '',
  assetWarrantyMonths: '',
  quantityReceived: 1,
  unitPurchasePrice: '',
  _key: nextLineKey(),
});

// A saved asset-receipt line as a form row.
const assetLineFrom = (l) => ({
  ...emptyAssetLine(),
  id: l.id,
  assetName: l.assetName || '',
  assetCategory: l.assetCategory || '',
  assetBrand: l.assetBrand || '',
  assetModel: l.assetModel || '',
  assetWarrantyMonths: l.assetWarrantyMonths ?? '',
  quantityReceived: l.quantityReceived ?? 1,
  unitPurchasePrice: l.unitPurchasePrice ?? '',
});

// New asset receipt, or an edit of a saved one. An edit reaches the assets the receipt created: the
// server writes a changed line onto its assets, adds assets for more units or a new row, and takes
// off only assets nobody has worked on for fewer units or a removed row.
const AssetReceiptFormModal = ({ existing = null, vendors, onClose, onSaved }) => {
  const notify = useToast();
  const isEdit = !!existing;
  const [vendorId, setVendorId] = useState(existing?.vendorId || '');
  const [invoiceNumber, setInvoiceNumber] = useState(existing?.invoiceNumber || '');
  const [invoiceDate, setInvoiceDate] = useState(() => existing?.invoiceDate || new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState(existing?.notes || '');
  // A receipt saved without its payment opens as "Not recorded" rather than quietly turning into Cash.
  const [paymentMode, setPaymentMode] = useState(isEdit ? (existing.paymentMode || '') : 'CASH');
  const [paymentReference, setPaymentReference] = useState(existing?.paymentReference || '');
  const [totalAmount, setTotalAmount] = useState(existing?.totalAmount != null ? String(existing.totalAmount) : '');
  const [lines, setLines] = useState(() =>
    (existing?.lines?.length ? existing.lines.map(assetLineFrom) : [emptyAssetLine()]));
  // Where focus goes after a row is removed; see removeLine.
  const addRowRef = useRef(null);
  const [saving, setSaving] = useState(false);
  // The bill and the transfer screenshot, under the same rules as a medicine receipt: a new receipt
  // needs the bill, and an edit cannot leave one that had a bill without it.
  const [keptBills, setKeptBills] = useState(existing?.bills || []);
  const [newBills, setNewBills] = useState([]);
  const billRequired = !isEdit || (existing?.bills || []).length > 0;
  const [paymentProofs, setPaymentProofs] = useState([]);
  const [paymentProofFiles, setPaymentProofFiles] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!existing?.id) return;
    let alive = true;
    paymentProofAPI.list('STOCK_RECEIPT', existing.id)
      .then(list => { if (alive) setPaymentProofs(Array.isArray(list) ? list : []); })
      .catch(() => { /* absent proof just means none attached */ });
    return () => { alive = false; };
  }, [existing?.id]);

  const linesSubtotal = useMemo(() => {
    return lines.reduce((sum, l) => {
      const qty = Number(l.quantityReceived) || 0;
      const cost = Number(l.unitPurchasePrice) || 0;
      return sum + qty * cost;
    }, 0);
  }, [lines]);

  const updateLine = (idx, field, value) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  const addLine = () => setLines((prev) => [...prev, emptyAssetLine()]);
  // Focus moves to Add row, rather than being left for whatever row takes this one's place.
  const removeLine = (idx) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
    requestAnimationFrame(() => addRowRef.current?.focus());
  };

  const validate = () => {
    if (!vendorId) return 'Please pick a vendor';
    if (!invoiceNumber.trim()) return 'Invoice number is required';
    if (!invoiceDate) return 'Invoice date is required';
    if (!totalAmount || Number(totalAmount) < 0) return 'Invoice total is required';
    if (lines.length === 0) return 'Add at least one line item';
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.assetName.trim()) return `Line ${i + 1}: asset name is required`;
      if (!l.assetCategory) return `Line ${i + 1}: category is required`;
      if (!l.quantityReceived || Number(l.quantityReceived) < 1) return `Line ${i + 1}: qty must be at least 1`;
      if (l.unitPurchasePrice === '' || Number(l.unitPurchasePrice) < 0) return `Line ${i + 1}: unit cost is required`;
    }
    if (paymentNeedsProof(paymentMode) && paymentProofs.length === 0 && paymentProofFiles.length === 0) {
      setFieldErrors(p => ({ ...p, paymentProof: 'Attach the payment screenshot for this method.' }));
      return 'Attach the payment screenshot for this method.';
    }
    if (billRequired && keptBills.length + newBills.length === 0) {
      const msg = isEdit
        ? 'This receipt has a bill copy. Attach a replacement before saving.'
        : "Attach a copy of the supplier's bill.";
      setFieldErrors(p => ({ ...p, bills: msg }));
      return msg;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { notify('warning', err); return; }
    setSaving(true);
    try {
      const payload = {
        receiptType: 'ASSET',
        vendorId,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        totalAmount: Number(totalAmount),
        notes: notes.trim() || null,
        paymentMode: paymentMode || null,
        paymentReference: paymentReference.trim() || null,
        lines: lines.map((l) => ({
          id: l.id || null,
          assetName: l.assetName.trim(),
          assetCategory: l.assetCategory,
          assetBrand: l.assetBrand.trim() || null,
          assetModel: l.assetModel.trim() || null,
          assetWarrantyMonths:
            l.assetWarrantyMonths === '' || l.assetWarrantyMonths == null
              ? null
              : Number(l.assetWarrantyMonths),
          quantityReceived: Number(l.quantityReceived),
          unitPurchasePrice: Number(l.unitPurchasePrice),
        })),
      };
      if (isEdit) {
        const saved = await stockReceiptAPI.update(
          existing.id, { ...payload, keepBillIds: keptBills.map(b => b.id) }, newBills, paymentProofFiles);
        const added = saved?.assetsAdded || 0;
        const removed = saved?.assetsRemoved || 0;
        const changes = [
          added ? `${added} asset${added === 1 ? '' : 's'} added` : null,
          removed ? `${removed} taken off the register` : null,
        ].filter(Boolean);
        notify('success', changes.length ? `Receipt updated: ${changes.join(', ')}` : 'Receipt updated');
      } else {
        await stockReceiptAPI.create(payload, newBills, paymentProofFiles);
        const totalAssets = lines.reduce((s, l) => s + (Number(l.quantityReceived) || 0), 0);
        notify('success', `Receipt saved - ${totalAssets} asset${totalAssets === 1 ? '' : 's'} added to inventory`);
      }
      onSaved();
    } catch (e) {
      notify('error', e.message || 'Failed to save asset receipt');
    } finally {
      setSaving(false);
    }
  };

  const assetReceiptDialogRef = useDialog(onClose);

  return (
    <div ref={assetReceiptDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        <div className="relative bg-gradient-to-r from-navy-800 to-navy-700 text-white p-6">
          <button aria-label="Close" onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-xl">
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Box className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {isEdit ? `Edit Asset Receipt ${existing.invoiceNumber || ''}`.trim() : 'New Asset Receipt'}
              </h2>
              <p className="text-white/80 text-sm">
                {isEdit
                  ? 'Changes are made on the assets this receipt created as well.'
                  : 'Records a delivery and auto-creates assets for each unit.'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Header fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor *</label>
              <Select
                value={vendorId}
                onChange={setVendorId}
                placeholder="Select vendor"
                options={vendors.map(v => ({ value: v.id, label: v.vendorName }))}
                buttonClassName="p-3 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice # *</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="e.g. INV-2026-0142"
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice Date *</label>
              <DatePicker value={invoiceDate} onChange={setInvoiceDate} />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">Line Items <span className="text-red-500">*</span></label>
              <button
                type="button"
                ref={addRowRef}
                onClick={addLine}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
              >
                <Plus className="h-4 w-4" /> Add row
              </button>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-visible bg-white">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-[19%]" />
                  <col className="w-[14%]" />
                  <col className="w-[12%]" />
                  <col className="w-[14%]" />
                  <col className="w-[7%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[4%]" />
                </colgroup>
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                    <th className="pl-4 pr-2 py-2.5 text-left">Asset Name</th>
                    <th className="pl-4 pr-2 py-2.5 text-left">Category</th>
                    <th className="pl-4 pr-2 py-2.5 text-left">Brand</th>
                    <th className="pl-4 pr-2 py-2.5 text-left">Model</th>
                    <th className="pl-2 pr-4 py-2.5 text-right border-l border-gray-200">Qty</th>
                    <th className="pl-2 pr-4 py-2.5 text-right">Unit Cost</th>
                    <th className="pl-2 pr-4 py-2.5 text-right">
                      Warranty <span className="normal-case font-normal tracking-normal text-gray-400">(mo)</span>
                    </th>
                    <th className="px-2 py-2.5 text-right border-l border-gray-200">Line Total</th>
                    <th className="px-2 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {lines.map((l, idx) => {
                    const qty = Number(l.quantityReceived) || 0;
                    const cost = Number(l.unitPurchasePrice) || 0;
                    const lineTotal = qty * cost;
                    return (
                      <tr key={l._key ?? idx} className="align-top">
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            value={l.assetName}
                            onChange={(e) => updateLine(idx, 'assetName', e.target.value)}
                            placeholder="e.g. Reception PC"
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Select
                            value={l.assetCategory}
                            onChange={(v) => updateLine(idx, 'assetCategory', v)}
                            placeholder="--"
                            options={ASSET_CATEGORY_OPTIONS}
                            buttonClassName="px-2 py-1.5 rounded-lg"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            value={l.assetBrand}
                            onChange={(e) => updateLine(idx, 'assetBrand', e.target.value)}
                            placeholder="e.g. Dell"
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            value={l.assetModel}
                            onChange={(e) => updateLine(idx, 'assetModel', e.target.value)}
                            placeholder="e.g. OptiPlex 7090"
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-2 align-middle border-l border-gray-100">
                          <input
                            type="number"
                            min="1"
                            value={l.quantityReceived}
                            onChange={(e) => updateLine(idx, 'quantityReceived', e.target.value)}
                            className="w-full px-2 py-1.5 text-right border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={l.unitPurchasePrice}
                            onChange={(e) => updateLine(idx, 'unitPurchasePrice', e.target.value)}
                            className="w-full px-2 py-1.5 text-right border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <input
                            type="number"
                            min="0"
                            value={l.assetWarrantyMonths}
                            onChange={(e) => updateLine(idx, 'assetWarrantyMonths', e.target.value)}
                            placeholder="none"
                            className="w-full px-2 py-1.5 text-right border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-2 text-right font-semibold text-gray-900 align-middle border-l border-gray-100">
                          {formatCurrency(lineTotal)}
                        </td>
                        <td className="px-2 py-2 text-center align-middle">
                          {lines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLine(idx)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              title="Remove row"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {isEdit
                ? 'A change to a line is made on its assets too. More units, or a new row, add assets; fewer units, or a removed row, take off assets nobody has worked on yet.'
                : 'Each line creates one Asset record per unit. Serial numbers can be added later on the per-asset page.'}
            </p>
          </div>

          {/* Payment capture */}
          <PaymentFields
            paymentMode={paymentMode}
            setPaymentMode={setPaymentMode}
            paymentReference={paymentReference}
            setPaymentReference={setPaymentReference}
            allowUnset={isEdit && !existing?.paymentMode}
          />
          {paymentNeedsProof(paymentMode) && (
            <PaymentProofUpload
              paymentMode={paymentMode}
              existing={paymentProofs}
              files={paymentProofFiles}
              onFiles={setPaymentProofFiles}
              error={fieldErrors.paymentProof}
              onClearError={() => setFieldErrors(p => ({ ...p, paymentProof: null }))}
            />
          )}

          {/* Notes + Invoice Total */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Partial delivery, remarks, etc."
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice Total *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold pointer-events-none">Rs.</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatAmountInput(totalAmount)}
                  onChange={(e) => setTotalAmount(parseAmountInput(e.target.value))}
                  placeholder="0.00"
                  className="w-full p-3 pl-12 border border-gray-200 rounded-xl text-right font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Lines sum: {formatCurrency(linesSubtotal)}</p>
            </div>
          </div>

          <BillCopyField
            keptBills={keptBills}
            setKeptBills={setKeptBills}
            newBills={newBills}
            setNewBills={setNewBills}
            required={billRequired}
            error={fieldErrors.bills}
            onClearError={() => setFieldErrors(p => ({ ...p, bills: null }))}
          />
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-xl font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 inline-flex items-center gap-2"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Box className="h-4 w-4" />}
            {isEdit ? 'Save Changes' : <>Save &amp; Add Assets</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// The supplier's bill on a receipt: the copies already attached (edit mode), which can be opened
// or removed, and the files picked now, uploaded when the receipt is saved. Shared by the medicine
// and asset receipt forms.
const BillCopyField = ({ keptBills, setKeptBills, newBills, setNewBills, required, error, onClearError }) => {
  const inputRef = useRef(null);
  const count = keptBills.length + newBills.length;
  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <label className="block text-sm font-semibold">
          Bill copy {required && <span className="text-red-500">*</span>}
        </label>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add file
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          if (picked.length) {
            setNewBills(prev => [...prev, ...picked]);
            onClearError?.();
          }
          // Cleared so picking the same file again still fires a change.
          e.target.value = '';
        }}
      />
      {count === 0 ? (
        <div
          className={`rounded-xl border border-dashed px-4 py-4 text-sm ${
            error ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-300 bg-gray-50 text-gray-500'
          }`}
        >
          Attach a photo or scan of the supplier&apos;s bill. A bill often runs to more than one
          page, so add as many files as it takes.
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {/* Already attached (edit mode) — these can be opened. */}
          {keptBills.map(b => (
            <div key={b.id} className="flex items-center gap-3 px-3 py-2 bg-white">
              <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <a
                href={authedMediaUrl(stockReceiptAPI.billFileUrl(b.id))}
                target="_blank"
                rel="noreferrer"
                className="flex-1 min-w-0 truncate text-sm text-emerald-700 hover:underline"
                title={b.originalFileName || 'Bill copy'}
              >
                {b.originalFileName || 'Bill copy'}
              </a>
              <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(b.sizeBytes)}</span>
              <button
                type="button"
                onClick={() => setKeptBills(prev => prev.filter(x => x.id !== b.id))}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
                title="Remove this copy"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {/* Picked in this session — not uploaded until the receipt is saved. */}
          {newBills.map((f, i) => (
            <div key={`new-${i}`} className="flex items-center gap-3 px-3 py-2 bg-emerald-50/40">
              <Paperclip className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate text-sm text-gray-800" title={f.name}>{f.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={() => setNewBills(prev => prev.filter((_, k) => k !== i))}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
                title="Remove this file"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

const ReceiptFormModal = ({ existing, prefill = null, vendors, medicines, canCreateMedicine = false, onMedicineAdded, onMedicineUpdated, onClose, onSaved }) => {
  const notify = useToast();
  const isEdit = !!existing;

  const [vendorId, setVendorId] = useState(existing?.vendorId || prefill?.vendorId || '');
  const [invoiceNumber, setInvoiceNumber] = useState(existing?.invoiceNumber || '');
  const [invoiceDate, setInvoiceDate] = useState(existing?.invoiceDate || todayIso());
  const [notes, setNotes] = useState(existing?.notes || '');
  // The purchase order this delivery answers. Orders are received here and only here: the order
  // works out what it has received from the receipt lines that point at it.
  // On a new receipt, no order chosen means "match it": the server receives it against the
  // supplier's order still waiting on its items. NO_ORDER says it answers none.
  const [orderId, setOrderId] = useState(existing?.orderId || prefill?.orderId || '');
  const [openOrders, setOpenOrders] = useState([]);
  // How this invoice states its discount and tax. Independent of each other, because a supplier can
  // give a flat rupee discount and still charge tax as a percentage. Legacy receipts carry no mode
  // and were all per-line percentages, so an absent value reads as PERCENT.
  // Bill copies. `keptBills` are the ones already attached (edit mode) that the user has not
  // removed; `newBills` are files picked in this session and not yet uploaded. A NEW receipt cannot
  // be saved without at least one, which is the whole point: the receipt is the claim and the bill
  // is the proof. A receipt that predates the rule can still be edited without one.
  // The transfer screenshot for a payment by wallet or bank transfer. Loaded on open in edit mode so
  // a receipt that already has one does not have to be re-proved to save an unrelated change.
  const [paymentProofs, setPaymentProofs] = useState([]);
  const [paymentProofFiles, setPaymentProofFiles] = useState([]);

  const [keptBills, setKeptBills] = useState(existing?.bills || []);
  const [newBills, setNewBills] = useState([]);
  const hadBillsOnOpen = (existing?.bills || []).length > 0;
  const billCount = keptBills.length + newBills.length;
  const billRequired = !isEdit || hadBillsOnOpen;

  // The supplier's sent orders still waiting on a delivery. Reloaded when the supplier changes, and
  // an order chosen for another supplier is let go, since a delivery can only fill its own
  // supplier's order. The order a receipt being edited already answers stays selectable even once
  // it is complete, so the receipt can be saved again.
  useEffect(() => {
    if (!vendorId) { setOpenOrders([]); return; }
    let alive = true;
    stockReceiptAPI.openOrders(vendorId)
      .then(list => {
        if (!alive) return;
        const rows = Array.isArray(list) ? list : [];
        setOpenOrders(rows);
        setOrderId(cur => (!cur || cur === NO_ORDER || cur === existing?.orderId || rows.some(o => o.id === cur)) ? cur : '');
      })
      .catch(() => { if (alive) setOpenOrders([]); });
    return () => { alive = false; };
  }, [vendorId, existing?.orderId]);

  const chosenOrder = useMemo(() => openOrders.find(o => o.id === orderId) || null, [openOrders, orderId]);
  const orderItemById = useMemo(() => {
    const m = new Map();
    (chosenOrder?.items || []).forEach(i => m.set(i.id, i));
    return m;
  }, [chosenOrder]);

  useEffect(() => {
    if (!existing?.id) return;
    let alive = true;
    paymentProofAPI.list('STOCK_RECEIPT', existing.id)
      .then(list => { if (alive) setPaymentProofs(Array.isArray(list) ? list : []); })
      .catch(() => { /* absent proof just means none attached */ });
    return () => { alive = false; };
  }, [existing?.id]);

  const [discountMode, setDiscountMode] = useState(existing?.discountMode || CHARGE_MODES.PERCENT);
  const [taxMode, setTaxMode] = useState(existing?.taxMode || CHARGE_MODES.PERCENT);
  // New receipts default to Cash; editing a legacy receipt (no mode recorded) starts blank so
  // saving doesn't silently stamp a guessed mode.
  const [paymentMode, setPaymentMode] = useState(existing ? (existing.paymentMode || '') : 'CASH');
  const [paymentReference, setPaymentReference] = useState(existing?.paymentReference || '');
  // totalAmount is editable so the user can match the actual bill — initialised to either the
  // existing receipt's total (edit) or the auto-sum of lines (new). A "Sync with lines" button
  // resets it back to the computed sum.
  const [totalAmount, setTotalAmount] = useState(
    existing?.totalAmount != null ? String(existing.totalAmount) : ''
  );
  const [totalManuallyOverridden, setTotalManuallyOverridden] = useState(isEdit);

  // Where focus goes after a line is removed; see removeLine.
  const addRowRef = useRef(null);
  const [lines, setLines] = useState(() => {
    if (existing?.lines?.length) {
      return existing.lines.map(l => ({
        _key: nextLineKey(),
        medicineId: l.medicineId,
        medicineDisplay: `${l.medicineName}${l.medicineGenericName ? ' • ' + l.medicineGenericName : ''}${l.medicineType ? ' • ' + typeLabel(l.medicineType) : ''}`,
        batchNumber: l.batchNumber || '',
        expiryDate: l.expiryDate || '',
        quantityReceived: String(l.quantityReceived ?? ''),
        // The bill's own line amount, as stored. It used to be rebuilt as listUnitPrice x quantity,
        // and that price is rounded to two decimals: Rs 2,006.00 over 120 tablets is 16.7167 each,
        // kept as 16.72, which multiplies back to Rs 2,006.40. Opening a receipt and saving it
        // therefore moved every line whose amount did not divide evenly into its quantity, and the
        // receipt quietly stopped matching the paper bill it was entered from.
        //
        // The two fallbacks are for lines written before the gross was stored. Where the invoice
        // stated its charges in rupees the gross is exactly recoverable from the net; otherwise
        // the old reconstruction is the best available, which is why the column exists.
        grossAmount: (() => {
          if (l.grossAmount != null) return String(l.grossAmount);
          if (l.lineTotal != null && (l.discountAmount != null || l.taxAmount != null)) {
            const net = Number(l.lineTotal) + Number(l.discountAmount || 0) - Number(l.taxAmount || 0);
            return String(Math.round(net * 100) / 100);
          }
          if (l.listUnitPrice != null && l.quantityReceived) {
            return String(Math.round(Number(l.listUnitPrice) * Number(l.quantityReceived) * 100) / 100);
          }
          return l.lineTotal != null ? String(l.lineTotal) : '';
        })(),
        // The rate field holds the LIST price. The response's unitPurchasePrice is the effective
        // cost after discount and tax, so loading that here would re-apply both on the next save.
        // Older lines predate listUnitPrice and had no discount/tax, where the two are the same.
        unitPurchasePrice: String(l.listUnitPrice ?? l.unitPurchasePrice ?? ''),
        unitSellingPrice: l.unitSellingPrice != null ? String(l.unitSellingPrice) : '',
        // Receipts saved before packs were counted separately carry only a unit quantity. Where it
        // divides evenly into packs the pack count is recoverable, so the row reopens filled in
        // rather than demanding the count be worked out again off the old bill.
        packsReceived: l.packsReceived != null
          ? String(l.packsReceived)
          : (l.unitsPerPackSnapshot > 1 && l.quantityReceived
             && l.quantityReceived % l.unitsPerPackSnapshot === 0
              ? String(l.quantityReceived / l.unitsPerPackSnapshot)
              : ''),
      discountPercent: l.discountPercent != null ? String(l.discountPercent) : '',
      taxPercent: l.taxPercent != null ? String(l.taxPercent) : '',
      discountAmount: l.discountAmount != null ? String(l.discountAmount) : '',
      taxAmount: l.taxAmount != null ? String(l.taxAmount) : '',
        packPrice: l.packPurchasePrice != null ? String(l.packPurchasePrice) : '',
        unitsPerPackSnapshot: l.unitsPerPackSnapshot ?? null,
        orderItemId: l.orderItemId ?? null,
        // A saved non-zero sell price is the user's own decision - never auto-overwrite it.
        sellManuallySet: l.unitSellingPrice != null && Number(l.unitSellingPrice) > 0,
      }));
    }
    return [blankLine()];
  });
  // What the server will match a new receipt to when no order is chosen: the same rule it applies,
  // so the form can say so before saving.
  const matchedOrder = useMemo(
    () => (isEdit || orderId ? null : matchOrder(openOrders, lines.map(l => l.medicineId))),
    [isEdit, orderId, openOrders, lines]
  );

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // Inline "add new medicine" — { lineIdx, name } identifies which line triggered it so the
  // newly created medicine can be selected straight back into that row.
  const [addMedicineFor, setAddMedicineFor] = useState(null);
  // Inline "configure the packaging" editor, opened from a line's hint. Carries the line index so
  // that line can be reset to pack-first entry once the medicine gains a units-per-pack factor.
  const [editMedicineFor, setEditMedicineFor] = useState(null);
  const [savingNewMedicine, setSavingNewMedicine] = useState(false);

  // Units-per-pack in force for a row. An edited receipt keeps the factor it was SAVED with, so
  // later changes to the medicine's pack configuration can never re-interpret this delivery.
  const rowFactor = useCallback(
    (l) => rowFactorFor(l, medicines.find(m => m.id === l.medicineId)),
    [medicines]);

  // Repackaged rows total as bottles x price-per-bottle EXACTLY. Deriving from the rounded per-unit
  // cost instead would drift off the supplier's bill (250/6 = 41.67, x6 = 250.02).
  // Gross for a line before any discount or tax: packs x pack price, or qty x list unit cost.
  // The gross is TYPED now, straight off the bill, rather than multiplied up from a rate. A supplier
  // states a line's amount; deriving it from a per-unit rate reintroduced exactly the rounding the
  // stated amount avoids.
  const rowGross = useCallback((l) => parseFloat(l.grossAmount) || 0, []);

  // A discontinued medicine cannot be ordered again, so it is not offered. The full list is still
  // what the rows look up against: a receipt being edited may already reference one, and dropping
  // it from the lookup would lose that line's pack factor and unit names.
  const selectableMedicines = useMemo(() => medicines.filter(m => !m.discontinued), [medicines]);

  // Packs are counted off the bill; the unit count follows from them. A medicine received loose has
  // no pack column, so its Qty IS the unit count and there is nothing to multiply.
  const rowShowsPacks = useCallback(
    (l) => !!packEntryLabelFor(medicines.find(m => m.id === l.medicineId)?.medicineType),
    [medicines]);
  const rowPacks = useCallback((l) => parseInt(l.packsReceived, 10) || 0, []);
  const rowUnits = useCallback(
    (l) => (rowShowsPacks(l) ? rowPacks(l) * rowFactor(l) : parseInt(l.quantityReceived, 10) || 0),
    [rowShowsPacks, rowPacks, rowFactor]);

  // What a line owes in discount and tax, as money. Which unit the figures are typed in is a
  // property of the receipt; the figures themselves are the line's own. Percentages are left
  // unrounded so a percentage receipt still rounds exactly once, at the net, and reproduces what it
  // always did. Mirrors StockReceiptService.chargesFor.
  const rowCharges = useCallback((l) => {
    const gross = rowGross(l);
    const discount = discountMode === CHARGE_MODES.AMOUNT
      ? Math.max(0, parseFloat(l.discountAmount) || 0)
      : gross * ((parseFloat(l.discountPercent) || 0) / 100);
    const afterDiscount = gross - discount;
    const tax = taxMode === CHARGE_MODES.AMOUNT
      ? Math.max(0, parseFloat(l.taxAmount) || 0)
      : afterDiscount * ((parseFloat(l.taxPercent) || 0) / 100);
    return { gross, discount, tax, net: Math.round((afterDiscount + tax) * 100) / 100 };
  }, [rowGross, discountMode, taxMode]);

  const rowTotal = useCallback((l) => rowCharges(l).net, [rowCharges]);

  // What a unit actually cost once discount and tax are settled. This is what gets stored, what
  // the medicine's cost rollup uses, and what the markup must price off — pricing off the list
  // cost would quietly overstate the margin by the discount and understate it by the tax.
  // Prefers net ÷ qty so it matches the server exactly; falls back to the per-unit formula while
  // a quantity has not been entered yet, so the suggestion still works mid-typing.
  const rowEffectiveUnitCost = useCallback((l) => {
    const units = rowUnits(l);
    if (units > 0) {
      const net = rowTotal(l);
      if (net > 0) return Math.round((net / units) * 100) / 100;
    }
    // Nothing to spread until a quantity is entered. There is no per-unit rate to fall back on any
    // more: the bill gives an amount, and an amount means nothing without the count it covers.
    return 0;
  }, [rowUnits, rowTotal]);

  // Fill a line with a selected/created medicine: id, display label, and last-known prices
  // (so the common "same prices as last time" case is one click). Repackaged medicines skip the
  // price prefill: their cost is only meaningful once the pack price is entered, and a stale
  // master price here would look like a real per-unit cost.
  const applyMedicineToLine = (idx, med) => {
    const display = `${med.name}${med.genericName ? ' • ' + med.genericName : ''}${med.medicineType ? ' • ' + typeLabel(med.medicineType) : ''}`;
    const repack = packFactorFor(med) > 1;
    setLines(prev => prev.map((row, i) =>
      i === idx
        ? {
            ...row,
            medicineId: med.id,
            medicineDisplay: display,
            // Selecting a different medicine resets the pack capture - the old factor no longer
            // applies, and the new medicine's live configuration takes over.
            packsReceived: '',
            packPrice: '',
            unitsPerPackSnapshot: null,
            // A different item fills a different order line, if any; the server matches it.
            orderItemId: null,
            quantityReceived: repack ? '' : row.quantityReceived,
            // Never pre-fill cost or price from the medicine master. A number the user didn't type
            // reads as "this delivery cost that", which is exactly the wrong thing to imply on a
            // receipt. The master's current values are surfaced as placeholders instead, and the
            // sell price auto-suggests from the markup % once a real cost is entered.
            unitPurchasePrice: repack ? '' : row.unitPurchasePrice,
            unitSellingPrice: repack ? '' : row.unitSellingPrice,
            sellManuallySet: repack ? false : row.sellManuallySet,
          }
        : row
    ));
  };

  // Create a medicine inline from the picker's "Register … as a new medicine" affordance, then select it
  // into the line that triggered it. Associates it with the receipt's vendor (if chosen) so the
  // new medicine has a sensible default supplier.
  const handleCreateMedicine = async (payload, modalVendorId) => {
    if (savingNewMedicine) return;
    setSavingNewMedicine(true);
    try {
      const created = await medicineAPI.create(payload, vendorId || modalVendorId || null);
      onMedicineAdded?.(created);
      if (addMedicineFor) applyMedicineToLine(addMedicineFor.lineIdx, created);
      notify('success', `"${created.name}" registered`);
      setAddMedicineFor(null);
    } catch (e) {
      notify('error', e.message || 'Failed to add medicine');
    } finally {
      setSavingNewMedicine(false);
    }
  };

  // Edit a medicine without leaving the receipt. The receipt modal stays mounted underneath, so
  // everything already typed into it survives; only the medicines list is refreshed so the line
  // picks up the new units-per-pack immediately.
  const handleUpdateMedicine = async (payload, modalVendorId, allowRepackChange) => {
    if (savingNewMedicine || !editMedicineFor) return;
    setSavingNewMedicine(true);
    try {
      const updated = await medicineAPI.update(
        editMedicineFor.medicine.id, payload, modalVendorId || null, allowRepackChange);
      onMedicineUpdated?.(updated);
      // Gaining a pack factor makes Qty and Rate derived and read-only, so anything typed into
      // them no longer applies — clear the row back to a clean pack-first entry.
      if (packFactorFor(updated) > 1) {
        setLines(prev => prev.map((row, i) => i === editMedicineFor.lineIdx
          ? { ...row, quantityReceived: '', unitPurchasePrice: '', packsReceived: '', packPrice: '', unitsPerPackSnapshot: null }
          : row));
      }
      notify('success', `"${updated.name}" updated`);
      setEditMedicineFor(null);
    } catch (e) {
      notify('error', e.message || 'Failed to update medicine');
    } finally {
      setSavingNewMedicine(false);
    }
  };

  // Auto-sum of all lines — used as a default and as the "Sync" target.
  const linesSum = useMemo(() => {
    return lines.reduce((acc, l) => acc + rowTotal(l), 0);
  }, [lines, rowTotal]);

  // While the user hasn't manually edited the total, keep it tracking the lines sum so
  // adding/removing rows feels live.
  useEffect(() => {
    if (!totalManuallyOverridden) {
      setTotalAmount(linesSum.toFixed(2));
    }
  }, [linesSum, totalManuallyOverridden]);

  // Global default profit markup % — used to auto-suggest the sell price from the unit cost.
  const [markupPct, setMarkupPct] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const s = await clinicalSettingsAPI.get();
        const pct = Number(s?.profitMarkupPercentage);
        if (!isNaN(pct)) setMarkupPct(pct);
      } catch { /* fall back to 0 — no markup suggestion */ }
    })();
  }, []);

  // Suggested sell price from a (per-dispensing-unit) cost using the global markup. Empty when
  // there's no cost or no markup configured.
  const sellFromCost = useCallback((cost) => {
    const c = parseFloat(cost);
    if (!(c > 0) || !(markupPct > 0)) return '';
    return (c * (1 + markupPct / 100)).toFixed(2);
  }, [markupPct]);

  // A sell price counts as "unset" when it's blank or zero — so a row loaded from an old receipt
  // (saved before the markup existed, hence sell = 0) is still eligible for an auto-suggestion.
  const sellUnset = (v) => v == null || v === '' || Number(v) === 0;

  // Once the markup loads, backfill a suggested sell price into any line that has a cost but no
  // sell price yet (covers Edit mode lines saved with sell = 0, and the markup arriving async).
  // User-entered (non-zero) sell prices are never touched.
  useEffect(() => {
    if (!(markupPct > 0)) return;
    setLines(prev => prev.map(l => {
      if (!sellUnset(l.unitSellingPrice)) return l;
      const suggested = sellFromCost(rowEffectiveUnitCost(l));
      return suggested ? { ...l, unitSellingPrice: suggested } : l;
    }));
  }, [markupPct, sellFromCost, rowEffectiveUnitCost]);

  const updateLine = (idx, field, value) => {
    setLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, [field]: value };
      // Typing a real sell price claims the field; clearing it hands it back to the markup.
      if (field === 'unitSellingPrice') {
        next.sellManuallySet = !sellUnset(value);
        return next;
      }
      // Anything that moves the REAL cost of a unit re-suggests the sell price: the list cost
      // itself, the quantity it is spread over, and the discount and tax that decide what was
      // actually paid. Previously only the cost did, so editing Disc % or Tax % silently left the
      // sell price priced off the pre-discount figure.
      const COST_FIELDS = ['unitPurchasePrice', 'quantityReceived', 'packsReceived', 'grossAmount',
        'discountPercent', 'taxPercent', 'discountAmount', 'taxAmount'];
      if (COST_FIELDS.includes(field) && !next.sellManuallySet && sellUnset(l.unitSellingPrice)) {
        next.unitSellingPrice = sellFromCost(rowEffectiveUnitCost(next));
      }
      return next;
    }));
  };


  // Focus moves to Add row, rather than being left for whatever line takes this one's place.
  const removeLine = (idx) => {
    setLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    requestAnimationFrame(() => addRowRef.current?.focus());
  };

  const addLine = () => setLines(prev => [...prev, blankLine()]);

  // Arriving from an order's Receive button, the order's items are put on the receipt straight away:
  // that is what the button was pressed for. Once only, so clearing them stays cleared.
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (prefillApplied.current || !prefill?.orderId || !chosenOrder || chosenOrder.id !== prefill.orderId) return;
    prefillApplied.current = true;
    addOrderItems();
    // addOrderItems reads chosenOrder, which is what this waits on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenOrder, prefill?.orderId]);

  // Puts a row on the receipt for every item the order is still waiting on. Only the item: what
  // arrived is counted off the bill, never assumed from what was ordered, so the counts stay blank
  // and each row shows what the order expected beside them.
  const addOrderItems = () => {
    if (!chosenOrder) return;
    setLines(prev => {
      const present = new Set(prev.map(l => l.medicineId).filter(Boolean));
      const kept = prev.filter(l => l.medicineId);
      const added = chosenOrder.items
        .filter(i => i.remaining > 0 && !present.has(i.medicineId))
        .map(i => {
          const med = medicines.find(m => m.id === i.medicineId);
          const display = med
            ? `${med.name}${med.genericName ? ' • ' + med.genericName : ''}${med.medicineType ? ' • ' + typeLabel(med.medicineType) : ''}`
            : i.medicineName;
          return { ...blankLine(), medicineId: i.medicineId, medicineDisplay: display, orderItemId: i.id };
        });
      const next = [...kept, ...added];
      return next.length ? next : [blankLine()];
    });
  };

  const validate = () => {
    const e = {};
    // The bill copy is a hard requirement on a new receipt: it is the only thing that lets the
    // figures be checked against the supplier's paper months later.
    // A payment that settles outside the system has to show the transfer.
    if (paymentNeedsProof(paymentMode) && paymentProofs.length === 0 && paymentProofFiles.length === 0) {
      e.paymentProof = 'Attach the payment screenshot for this method.';
    }
    if (billRequired && billCount === 0) {
      e.bills = isEdit
        ? 'This receipt has a bill copy. Attach a replacement before saving.'
        : "Attach a copy of the supplier's bill.";
    }
    if (!vendorId) e.vendorId = 'Vendor is required';
    if (!invoiceNumber.trim()) e.invoiceNumber = 'Invoice number is required';
    if (!invoiceDate) e.invoiceDate = 'Invoice date is required';
    if (!totalAmount || parseFloat(totalAmount) < 0) e.totalAmount = 'Total is required';

    const lineErrs = lines.map((l) => {
      const errs = {};
      if (!l.medicineId) errs.medicineId = 'Select a medicine';
      // Whichever count the row asks for. A packed medicine is counted in packs and its Qty is
      // derived; a loose one is counted in units.
      if (rowShowsPacks(l)) {
        const packs = parseInt(l.packsReceived, 10);
        if (!packs || packs < 1) errs.packsReceived = 'Enter how many were received';
      } else {
        const qty = parseInt(l.quantityReceived, 10);
        if (!qty || qty < 1) errs.quantityReceived = 'Qty must be at least 1';
      }
      // A gross of exactly 0 is real — suppliers give free goods, and those units still arrived.
      // Only a blank or negative amount is an error.
      const gross = parseFloat(l.grossAmount);
      if (l.grossAmount === '' || isNaN(gross) || gross < 0) errs.grossAmount = 'Enter the gross amount';
      return errs;
    });
    if (lineErrs.some(e => Object.keys(e).length > 0)) e.lines = lineErrs;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      notify('warning', 'Please fix the highlighted errors');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        vendorId,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        totalAmount: parseFloat(totalAmount),
        notes: notes.trim() || null,
        // The server re-derives every line total and per-unit cost from these, so the arithmetic
        // has one implementation rather than trusting the browser's.
        discountMode,
        taxMode,
        // Which already-attached copies survive this edit. Always sent, so the server never has to
        // guess whether an omission meant "keep them" or "drop them".
        keepBillIds: keptBills.map(b => b.id),
        paymentMode: paymentMode || null,
        paymentReference: paymentReference.trim() || null,
        orderId: orderId && orderId !== NO_ORDER ? orderId : null,
        // Said to answer no order, so the server does not match it to one.
        noOrder: orderId === NO_ORDER,
        lines: lines.map(l => {
          const isPackRow = rowShowsPacks(l);
          return {
            medicineId: l.medicineId,
            // The server matches a line to the order by item where none is named.
            orderItemId: orderId && orderId !== NO_ORDER ? (l.orderItemId || null) : null,
            batchNumber: l.batchNumber.trim() || null,
            expiryDate: l.expiryDate || null,
            // A packed medicine's count goes up as packs and the server multiplies it by the
            // units-per-pack; a loose one sends its unit count instead. Only ever one of the two,
            // so the server never has to reconcile a pair that disagree.
            quantityReceived: isPackRow ? null : parseInt(l.quantityReceived, 10),
            grossAmount: l.grossAmount !== '' ? parseFloat(l.grossAmount) : null,
            unitPurchasePrice: parseFloat(l.unitPurchasePrice),
            unitSellingPrice: l.unitSellingPrice !== '' ? parseFloat(l.unitSellingPrice) : null,
            // Percentages. The server applies discount then tax and derives the line total plus the
            // effective per-unit cost from the net.
            // Only the pair the receipt is stated in: a line must never carry a percentage and an
            // amount for the same charge and leave the reader to guess which one was meant.
            discountPercent: discountMode === CHARGE_MODES.PERCENT && l.discountPercent !== ''
              ? parseFloat(l.discountPercent) : null,
            taxPercent: taxMode === CHARGE_MODES.PERCENT && l.taxPercent !== ''
              ? parseFloat(l.taxPercent) : null,
            discountAmount: discountMode === CHARGE_MODES.AMOUNT && l.discountAmount !== ''
              ? parseFloat(l.discountAmount) : null,
            taxAmount: taxMode === CHARGE_MODES.AMOUNT && l.taxAmount !== ''
              ? parseFloat(l.taxAmount) : null,
            // Pack truth travels with the line so the server can re-derive the quantity, freeze the
            // factor, and total the line without rounding drift.
            packsReceived: isPackRow ? parseInt(l.packsReceived, 10) : null,
            // Tested against '' rather than truthiness: a free-goods line has a pack price of
            // exactly 0, and dropping it to null here would make the server reject the line for
            // having packs with no price.
          };
        }),
      };
      let saved;
      if (isEdit) {
        saved = await stockReceiptAPI.update(existing.id, payload, newBills, paymentProofFiles);
        notify('success', 'Stock receipt updated');
      } else {
        saved = await stockReceiptAPI.create(payload, newBills, paymentProofFiles);
        notify('success', saved?.orderMatched && saved?.orderNumber
          ? `Stock receipt recorded and received against ${saved.orderNumber}, the order waiting on these items`
          : 'Stock receipt recorded - inventory updated');
      }
      // The saved receipt travels back so the caller can attach the scanned bill to it.
      onSaved(saved);
    } catch (e) {
      notify('error', e.message || 'Failed to save receipt');
    } finally {
      setSubmitting(false);
    }
  };

  const receiptFormDialogRef = useDialog(onClose);

  return (
    <div ref={receiptFormDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
              {isEdit ? <Edit2 className="w-5 h-5 text-white" /> : <Plus className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{isEdit ? 'Edit Stock Receipt' : 'New Stock Receipt'}</h2>
              <p className="text-white/80 text-sm">Record a delivery from a vendor</p>
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5 flex-1">
          {/* Header fields */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-1">Vendor <span className="text-red-500">*</span></label>
              <Select
                value={vendorId}
                onChange={setVendorId}
                placeholder="Select vendor"
                options={vendors.map(v => ({ value: v.id, label: v.vendorName }))}
                error={errors.vendorId ? ' ' : undefined}
                buttonClassName="p-2.5 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Invoice # <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={e => setInvoiceNumber(e.target.value)}
                className={`w-full p-2.5 border rounded-lg ${errors.invoiceNumber ? 'border-red-300' : 'border-gray-200'}`}
                placeholder="e.g. INV-2026-0142"
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Invoice Date <span className="text-red-500">*</span></label>
              <DatePicker
                value={invoiceDate}
                onChange={setInvoiceDate}
                placeholder="Select date"
                max={todayIso()}
                error={!!errors.invoiceDate}
              />
            </div>
          </div>

          {/* A delivery that answers a purchase order is received against it here; this is the
              only way an order is received. Offered only when the supplier has an order waiting. */}
          {vendorId && (openOrders.length > 0 || orderId) && (
            <div className="rounded-xl border border-navy-100 bg-navy-50/40 p-4 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-1">Against order</label>
                <Select
                  value={orderId}
                  onChange={setOrderId}
                  placeholder={isEdit ? 'Not against an order' : 'Match it to the waiting order'}
                  options={[
                    ...(isEdit
                      ? [{ value: '', label: 'Not against an order' }]
                      : [
                        { value: '', label: 'Match it to the waiting order' },
                        { value: NO_ORDER, label: 'Not against any order' },
                      ]),
                    ...openOrders.map(o => ({
                      value: o.id,
                      label: `${o.orderNumber}${o.orderDate ? ` · sent ${formatDate(o.orderDate)}` : ''} · ${o.items.filter(i => i.remaining > 0).length} item(s) due`,
                    })),
                    ...(orderId && !openOrders.some(o => o.id === orderId) && existing?.orderNumber
                      ? [{ value: orderId, label: existing.orderNumber }]
                      : []),
                  ]}
                  buttonClassName="p-2.5 rounded-lg"
                />
              </div>
              <div className="md:col-span-2 flex items-center gap-3 flex-wrap">
                {chosenOrder && (
                  <button
                    type="button"
                    onClick={addOrderItems}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-200 bg-white text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                  >
                    <ListChecks className="w-4 h-4" /> Add the order's items
                  </button>
                )}
                <p className="text-xs text-gray-600 flex-1 min-w-[12rem]">
                  {chosenOrder
                    ? 'Count each line off the bill. Whatever arrived is set against the order, and anything the order did not ask for is received as an extra.'
                    : orderId === NO_ORDER
                      ? 'Received as a delivery nobody ordered. It shows as not on any order in the Orders vs Deliveries report.'
                      : isEdit
                        ? 'This supplier has an order waiting. Choose it if this delivery answers it.'
                        : matchedOrder
                          ? `These items are waiting on ${matchedOrder.orderNumber}: the delivery is received against it on saving. Choose another order, or Not against any order, if it does not answer it.`
                          : 'Saved against the order still waiting on these items, if there is one. Add the items, or choose the order yourself.'}
                </p>
              </div>
            </div>
          )}

          {/* Lines grid */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <label className="text-sm font-semibold">Line Items <span className="text-red-500">*</span></label>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Say what unit this particular invoice prints its charges in before entering the
                    lines. It applies to every line on the receipt; the figures are still typed per
                    line. Discount and tax are set separately because an invoice can mix the two. */}
                <ChargeModeToggle label="Discount" mode={discountMode} onMode={setDiscountMode} />
                <ChargeModeToggle label="Tax" mode={taxMode} onMode={setTaxMode} />
                <button
                  type="button"
                  ref={addRowRef}
                  onClick={addLine}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add row
                </button>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl bg-white overflow-visible">
              {/* table-fixed + an explicit colgroup: under auto layout the medicine name stretched
                  column one and pushed every header off the field it labels. */}
              <table className="w-full table-fixed">
                {/* Expiry holds a full date plus the picker's icon, so it stays wide: "Aug..."
                    instead of a readable date defeats the point of showing it. Batch is no longer
                    squeezed for it, though. A real batch number is 8-12 characters and was being
                    cropped to five, which makes the field unreadable while typing and unverifiable
                    against the carton afterwards. The width comes out of the medicine name, which
                    truncates gracefully and is picked from a dropdown rather than read. */}
                <colgroup>
                  <col className="w-[15%]" />
                  <col className="w-[11%]" />
                  <col className="w-[13%]" />
                  <col className="w-[10%]" />
                  <col className="w-[6%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[5%]" />
                </colgroup>
                <thead className="bg-gray-50 border-b border-gray-200">
                  {/* Group band. A line reads left to right as three stages: what arrived, what the
                      supplier billed for it, and what one unit ends up costing and selling for. */}
                  <tr className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    <th colSpan={3} className="pl-5 pr-3 pt-2.5 pb-1 text-left">Item</th>
                    <th colSpan={5} className="px-2 pt-2.5 pb-1 text-center border-l border-gray-200">As billed by supplier</th>
                    <th colSpan={2} className="px-3 pt-2.5 pb-1 border-l border-gray-200"></th>
                  </tr>
                  {/* Each header's padding is the cell padding plus the field's own padding, so the
                      label sits over the value it names instead of over the input's outer edge. */}
                  <tr className="text-[11px] font-semibold text-gray-600 uppercase">
                    <th className="pl-5 pr-3 pb-2.5 text-left">
                      Medicine <span className="normal-case font-normal text-gray-400">(Name • Generic)</span>
                    </th>
                    <th className="pl-5 pr-3 pb-2.5 text-left">Batch #</th>
                    <th className="pl-6 pr-3 pb-2.5 text-left">Expiry</th>
                    <th
                      className="pl-2 pr-4 pb-2.5 text-right border-l border-gray-200"
                      title="How many packs or bottles the distributor delivered on this line. Each row says which it counts."
                    >
                      Received
                    </th>
                    <th
                      className="px-2 pb-2.5 text-center"
                      title="Units this line puts into stock, worked out from the count received wherever a medicine comes in packs or bottles."
                    >
                      Qty
                    </th>
                    <th className="pl-2 pr-4 pb-2.5 text-right" title="This line's amount exactly as the supplier's bill prints it, before discount and tax.">
                      Gross Amount
                    </th>
                    <th
                      className="pl-2 pr-4 pb-2.5 text-right"
                      title={discountMode === CHARGE_MODES.AMOUNT
                        ? 'Trade discount on this line in rupees, taken off the gross before tax'
                        : 'Trade discount the supplier gave on this line, taken off the gross before tax'}
                    >
                      {discountMode === CHARGE_MODES.AMOUNT ? 'Disc Rs' : 'Disc %'}
                    </th>
                    <th
                      className="pl-2 pr-4 pb-2.5 text-right"
                      title={taxMode === CHARGE_MODES.AMOUNT
                        ? 'Sales tax on this line in rupees, charged on the discounted amount'
                        : 'Sales tax on this line, charged on the discounted amount'}
                    >
                      {taxMode === CHARGE_MODES.AMOUNT ? 'Tax Rs' : 'Tax %'}
                    </th>
                    <th className="px-3 pb-2.5 text-right border-l border-gray-200" title="Net payable for this line, matching the supplier's invoice">
                      Line Total
                    </th>
                    <th className="px-3 pb-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const lineErr = errors.lines?.[idx] || {};
                    const lineTotal = rowTotal(l);
                    const effectiveUnitCost = rowEffectiveUnitCost(l);
                    const med = medicines.find(m => m.id === l.medicineId);
                    // Edit mode keeps the factor the receipt was saved with; new rows read the
                    // medicine's live configuration.
                    const factor = rowFactorFor(l, med);
                    // A liquid is described by its two sizes; a solid by how many tablets are in
                    // the pack or strip. Same factor underneath, different words for the user.
                    const isSolidPack = med && SOLID_PACK_TYPES.has(med.medicineType);
                    const packNoun = isSolidPack ? solidPackNoun(med.packagingType) : 'bottle';
                    const unitNoun = isSolidPack ? solidUnitNoun(med.medicineType).toLowerCase() : 'units';
                    const packLabel = med && med.packSize && med.dispenseSize
                      ? `${med.packSize}${med.sizeUnit || ''} → ${med.dispenseSize}${med.sizeUnit || ''}`
                      : null;
                    // Could be bought by the pack, but nobody has configured it yet. Without this
                    // the pack inputs are simply absent, which reads as a missing feature.
                    const showSetupHint = factor === 1 && med
                      && (REPACKAGEABLE_TYPES.has(med.medicineType) || isSolidPack);
                    const packErr = lineErr.packsReceived || lineErr.packPrice;
                    // "Packs" for tablets, capsules and consumables, "Bottles" for liquids, and
                    // nothing at all for a medicine that arrives loose.
                    const packEntryLabel = med ? packEntryLabelFor(med.medicineType) : null;
                    return (
                      <React.Fragment key={l._key ?? idx}>
                        <tr className={idx > 0 ? 'border-t border-gray-200' : ''}>
                          <td className="px-3 py-2.5 align-middle">
                            <MedicinePicker
                              medicines={selectableMedicines}
                              value={l.medicineId}
                              display={l.medicineDisplay}
                              error={!!lineErr.medicineId}
                              canAddNew={canCreateMedicine}
                              onAddNew={(name) => setAddMedicineFor({ lineIdx: idx, name })}
                              onSelect={(med2) => applyMedicineToLine(idx, med2)}
                            />
                            {chosenOrder && l.medicineId && (() => {
                              const due = (l.orderItemId && orderItemById.get(l.orderItemId))
                                || chosenOrder.items.find(i => i.medicineId === l.medicineId);
                              return due ? (
                                <p className="mt-1 text-xs text-navy-700">
                                  On order: {due.quantity.toLocaleString()}{due.received > 0 ? `, ${due.received.toLocaleString()} already in` : ''}
                                </p>
                              ) : (
                                <p className="mt-1 text-xs text-gray-500">Not on this order: received as an extra</p>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2.5 align-middle">
                            <input
                              type="text"
                              value={l.batchNumber}
                              onChange={e => updateLine(idx, 'batchNumber', e.target.value)}
                              className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                              placeholder="Batch"
                              maxLength={50}
                            />
                          </td>
                          <td className="px-3 py-2.5 align-middle">
                            <DatePicker
                              value={l.expiryDate}
                              onChange={(v) => updateLine(idx, 'expiryDate', v)}
                              placeholder="Expiry"
                            />
                          </td>
                          {/* What was counted off the bill, and what that comes to in stock. A
                              medicine delivered in packs or bottles is counted that way and its unit
                              quantity follows; one delivered loose has no count to follow from, so
                              Qty is typed and this cell stays empty. */}
                          <td className="px-2 py-2.5 align-middle border-l border-gray-100">
                            {packEntryLabel ? (
                              <div className="relative">
                                <input
                                  type="number"
                                  min="1"
                                  value={l.packsReceived}
                                  onChange={e => updateLine(idx, 'packsReceived', e.target.value)}
                                  title={packEntryLabel + ' received from the distributor on this line'}
                                  className={`w-full p-2 border rounded-lg text-sm text-right ${lineErr.packsReceived ? 'border-red-300' : 'border-gray-200'}`}
                                />
                                <span className="absolute inset-x-0 top-full mt-0.5 text-[10px] text-gray-400 text-center leading-none">
                                  {packEntryLabel}
                                </span>
                              </div>
                            ) : (
                              <div className="p-2 text-sm text-center text-gray-300">-</div>
                            )}
                          </td>
                          <td className="px-2 py-2.5 align-middle">
                            {packEntryLabel ? (
                              <div
                                className="p-2 text-sm text-center text-gray-700 tabular-nums"
                                title={`${rowPacks(l)} ${packEntryNoun(med.medicineType, rowPacks(l))} of ${factor} ${unitNoun.toLowerCase()}`}
                              >
                                {rowUnits(l) || '-'}
                              </div>
                            ) : (
                              <input
                                type="number"
                                min="1"
                                value={l.quantityReceived}
                                onChange={e => updateLine(idx, 'quantityReceived', e.target.value)}
                                title="Units received on this line"
                                className={`w-full p-2 border rounded-lg text-sm text-right ${lineErr.quantityReceived ? 'border-red-300' : 'border-gray-200'}`}
                              />
                            )}
                          </td>
                          <td className="px-2 py-2.5 align-middle">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={l.grossAmount}
                              onChange={e => updateLine(idx, 'grossAmount', e.target.value)}
                              placeholder="amount"
                              title="This line's amount exactly as the bill prints it, before discount and tax"
                              className={`w-full p-2 border rounded-lg text-sm text-right ${lineErr.grossAmount ? 'border-red-300' : 'border-gray-200'}`}
                            />
                          </td>
                          {/* The unit is the receipt's choice; the figure is always this line's
                              own, so both modes are an ordinary input. Only the pair matching the
                              mode is sent, so a line never carries a percentage and an amount for
                              the same charge. */}
                          <td className="px-2 py-2.5 align-middle">
                            {discountMode === CHARGE_MODES.AMOUNT ? (
                              <input
                                type="number" min="0" step="0.01"
                                value={l.discountAmount}
                                onChange={e => updateLine(idx, 'discountAmount', e.target.value)}
                                placeholder="0.00"
                                title="Trade discount on this line in rupees. Taken off the gross before tax."
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm text-right"
                              />
                            ) : (
                              <input
                                type="number" min="0" max="100" step="0.01"
                                value={l.discountPercent}
                                onChange={e => updateLine(idx, 'discountPercent', e.target.value)}
                                placeholder="0"
                                title="Trade discount the supplier gave on this line. Taken off the gross before tax."
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm text-right"
                              />
                            )}
                          </td>
                          <td className="px-2 py-2.5 align-middle">
                            {taxMode === CHARGE_MODES.AMOUNT ? (
                              <input
                                type="number" min="0" step="0.01"
                                value={l.taxAmount}
                                onChange={e => updateLine(idx, 'taxAmount', e.target.value)}
                                placeholder="0.00"
                                title="Sales tax on this line in rupees, charged on the discounted amount."
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm text-right"
                              />
                            ) : (
                              <input
                                type="number" min="0" max="100" step="0.01"
                                value={l.taxPercent}
                                onChange={e => updateLine(idx, 'taxPercent', e.target.value)}
                                placeholder="0"
                                title="Sales tax charged on this line, applied after the discount."
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm text-right"
                              />
                            )}
                          </td>
                          {/* Purchase and Selling per unit have moved to the strip below this row,
                              where they get room to be labelled. They belong together with the pack
                              maths that produces them, and the invoice columns above are then only
                              the figures copied off the supplier's bill. */}
                          <td className="px-3 py-2.5 align-middle text-right font-semibold text-sm text-gray-900 border-l border-gray-100">
                            {formatCurrency(lineTotal)}
                          </td>
                          <td className="px-3 py-2.5 align-middle text-center">
                            {lines.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLine(idx)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                title="Remove row"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                        {/* One box under every line, always present, holding everything that is
                            worked out rather than copied off the invoice: the pack entry (or the
                            hint offering it) on the left, and the two per-unit figures on the right.
                            These were columns until they crowded the grid — at eleven columns the
                            batch number and the medicine name were both cropping — and a derived
                            figure does not belong in the same row as the values it is derived from.
                            The box is here even for a medicine with no pack setup, so Purchase and
                            Selling are in the same place on every line. */}
                        <tr>
                          <td colSpan={10} className="px-3 pt-1.5 pb-2.5">
                            <div className={`flex flex-wrap items-end gap-x-4 gap-y-2 rounded-lg border px-3 py-2 ${packErr ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-100'}`}>
                              <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                              {factor === 1 && !showSetupHint ? null : factor > 1 ? (
                                /* Just what one pack holds, as set when the medicine was
                                   registered. It is the one fact the person entering the delivery
                                   cannot read off the bill in front of them; the rest of the
                                   arithmetic lands in Purchase / unit at the end of the strip. */
                                <>
                                  <div className="text-[11px] font-medium text-blue-800 pb-1.5">
                                    {isSolidPack
                                      ? `1 ${packNoun} = ${factor} ${unitNoun}`
                                      : `Repackaged${packLabel ? `: ${packLabel}` : ''} (${factor} units per ${packNoun})`}
                                  </div>
                                </>
                              ) : (
                                <div className="text-[11px] text-gray-600 pb-1">
                                  {isSolidPack ? (
                                    <>
                                      Counted as single {unitNoun}. To buy by the {packNoun} and have the count and
                                      per-{unitNoun.replace(/s$/, '')} cost worked out for you,{' '}
                                      <button
                                        type="button"
                                        onClick={() => setEditMedicineFor({ lineIdx: idx, medicine: med })}
                                        className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                                      >
                                        set {solidUnitNoun(med.medicineType)} per {packNoun}
                                      </button>{' '}
                                      on this medicine.
                                    </>
                                  ) : (
                                    <>
                                      Received in whole bottles. To receive by distributor bottle and have the units and
                                      per-unit cost worked out for you,{' '}
                                      <button
                                        type="button"
                                        onClick={() => setEditMedicineFor({ lineIdx: idx, medicine: med })}
                                        className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                                      >
                                        set Repackaging
                                      </button>{' '}
                                      on this medicine.
                                    </>
                                  )}
                                </div>
                              )}
                              </div>
                              {/* Per unit: what one unit actually cost once the invoice's discount
                                  and tax are settled, and what it will be sold for. Purchase is
                                  derived and never typed; Selling is suggested from it by the
                                  markup % and stays editable so a delivery can be priced by hand. */}
                              <div className="flex items-end gap-3">
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">
                                    Purchase / unit
                                  </label>
                                  <div
                                    className="w-28 p-1.5 rounded-md text-xs text-right font-semibold bg-emerald-50 border border-emerald-300 text-emerald-800"
                                    title="Rate after discount and tax: this line's total divided by the quantity. Calculated, not editable."
                                  >
                                    {effectiveUnitCost > 0
                                      ? effectiveUnitCost.toFixed(2)
                                      : <span className="text-emerald-400 font-normal">&mdash;</span>}
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">
                                    Selling / unit
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={l.unitSellingPrice}
                                    onChange={e => updateLine(idx, 'unitSellingPrice', e.target.value)}
                                    className="w-28 p-1.5 bg-white border border-gray-200 rounded-md text-xs text-right"
                                    placeholder={med && Number(med.sellingPrice) > 0 ? `now ${Number(med.sellingPrice).toFixed(2)}` : 'auto'}
                                    title="Patient-facing price per unit. Suggested from the purchase price using the markup %. Leave blank to keep the medicine's current selling price."
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* The glyphs only carry the chain as far as the line total. Purchase and Selling sit
                on the strip under each line, so say plainly where they come from. */}
            <p className="mt-2 text-[11px] text-gray-500">
              Line total = gross amount, less discount, plus tax. Purchase per unit is that line
              total divided by the units received, and selling is suggested from it using the
              markup %.
            </p>
            {errors.lines && (
              <p className="text-red-500 text-xs mt-1">Fix the line errors above</p>
            )}
          </div>

          {/* Payment capture */}
          <PaymentFields
            paymentMode={paymentMode}
            setPaymentMode={setPaymentMode}
            paymentReference={paymentReference}
            setPaymentReference={setPaymentReference}
            allowUnset={isEdit && !existing?.paymentMode}
          />
          {paymentNeedsProof(paymentMode) && (
            <PaymentProofUpload
              paymentMode={paymentMode}
              existing={paymentProofs}
              files={paymentProofFiles}
              onFiles={setPaymentProofFiles}
              error={errors.paymentProof}
              onClearError={() => setErrors(p => ({ ...p, paymentProof: null }))}
            />
          )}

          {/* Total + notes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows="2"
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm"
                placeholder="Partial delivery, remarks, etc."
                maxLength={1000}
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-semibold mb-1 flex items-center justify-between">
                <span>Invoice Total <span className="text-red-500">*</span></span>
                {totalManuallyOverridden && Math.abs(parseFloat(totalAmount || '0') - linesSum) > 0.01 && (
                  <button
                    onClick={() => { setTotalAmount(linesSum.toFixed(2)); setTotalManuallyOverridden(false); }}
                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Sync with lines
                  </button>
                )}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold pointer-events-none">Rs.</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatAmountInput(totalAmount)}
                  onChange={e => { setTotalAmount(parseAmountInput(e.target.value)); setTotalManuallyOverridden(true); }}
                  className={`w-full p-2.5 pl-12 border rounded-lg text-right font-semibold ${errors.totalAmount ? 'border-red-300' : 'border-gray-200'}`}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Lines sum: {formatCurrency(linesSum)}
                {totalManuallyOverridden && Math.abs(parseFloat(totalAmount || '0') - linesSum) > 0.01 && (
                  <span className="text-amber-600"> · differs from lines</span>
                )}
              </p>
            </div>
          </div>

          {/* Bill copy — the supplier's paper, photographed or scanned. Required on a new receipt:
              the receipt is the claim and this is the proof, and an audit months later has nothing
              to check the typed figures against without it. A receipt entered before this was
              required can still be edited without one, so historical data stays correctable. */}
          <BillCopyField
            keptBills={keptBills}
            setKeptBills={setKeptBills}
            newBills={newBills}
            setNewBills={setNewBills}
            required={billRequired}
            error={errors.bills}
            onClearError={() => setErrors(p => ({ ...p, bills: null }))}
          />
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 bg-gradient-to-r from-navy-800 to-navy-700 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {submitting ? 'Saving…' : (isEdit ? 'Update Receipt' : 'Save & Update Inventory')}
          </button>
        </div>
      </div>

      {/* Inline register-medicine — reuses the shared Register Medicine form, prefilled with the
          typed name. On save the new medicine is created, added to the list, and selected into the row. */}
      {addMedicineFor && (
        <MedicineFormModal
          initialName={addMedicineFor.name}
          vendors={vendors}
          onClose={() => setAddMedicineFor(null)}
          onSubmit={handleCreateMedicine}
        />
      )}

      {/* Inline edit — opened from a line's "not configured" hint so the packaging can be set up
          without abandoning a half-filled receipt. Rendered over the receipt, which stays mounted. */}
      {editMedicineFor && (
        <MedicineFormModal
          medicine={editMedicineFor.medicine}
          vendors={vendors}
          onClose={() => setEditMedicineFor(null)}
          onSubmit={handleUpdateMedicine}
        />
      )}
    </div>
  );
};

// =============================== MEDICINE PICKER ===============================

const MedicinePicker = ({ medicines, value, display, onSelect, error, onAddNew, canAddNew = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Keyboard highlight in the list. -1 is none, so Enter on a half-typed name picks nothing.
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  const openPicker = () => setOpen(o => !o);

  // Escape closes the list and nothing else. Taken in the capture phase and stopped, so the receipt
  // dialog around the picker stays open; focus goes back to the picker's own button.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      setQuery('');
      wrapperRef.current?.querySelector('button')?.focus();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  // Close on outside click — same pattern used elsewhere in the app.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return medicines.slice(0, 50);
    return medicines.filter(m =>
      m.name?.toLowerCase().includes(q)
      || m.genericName?.toLowerCase().includes(q)
      || m.medicineCode?.toLowerCase().includes(q)
      // The dosage form too, so "syrup" finds the syrups. Without it only "syr" worked, and only
      // because it matched the MED-SYR- codes by accident.
      || m.medicineType?.toLowerCase().replace('_', ' ').includes(q)
    ).slice(0, 50);
  }, [medicines, query]);

  // A new query changes the rows under the highlight, and a reopened list starts with none.
  useEffect(() => { setHighlight(-1); }, [query, open]);

  // Keep the highlighted entry in view while arrowing through a list taller than its panel.
  useEffect(() => {
    if (highlight < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-opt="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  // One path per entry, so a click and an Enter do exactly the same thing.
  const pickMedicine = (m) => { if (m.id !== value) markDialogDirty(wrapperRef.current); onSelect(m); setOpen(false); setQuery(''); };
  const registerNew = () => { onAddNew(query.trim()); setOpen(false); setQuery(''); };

  // Closing from the keyboard puts focus back on the picker's button first. Left in the search box as
  // the list unmounts, focus falls to <body> and the dialog sends the next Tab to its first field.
  const focusButton = () => wrapperRef.current?.querySelector('button')?.focus();

  // Entries: the filtered medicines, then the register-new button when it is shown.
  const optionCount = filtered.length + (canAddNew && onAddNew ? 1 : 0);
  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, optionCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.min(Math.max(h - 1, 0), optionCount - 1));
    } else if (e.key === 'Enter') {
      if (highlight < 0) return;
      e.preventDefault();
      if (highlight < filtered.length) { focusButton(); pickMedicine(filtered[highlight]); }
      else if (highlight === filtered.length && canAddNew && onAddNew) { focusButton(); registerNew(); }
    } else if (e.key === 'Tab') {
      // Not prevented, so the browser's Tab moves on from the button.
      focusButton();
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={openPicker}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full p-2 border rounded-lg text-sm text-left flex items-center justify-between transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200 ${
          error ? 'border-red-300' : open ? 'border-emerald-400 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <span className={`truncate ${value ? 'text-gray-900' : 'text-gray-400'}`}>
          {display || 'Search a medicine…'}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-72 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                autoFocus
                data-no-dirty
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                role="combobox"
                aria-expanded={open}
                aria-controls="med-picker-list"
                aria-activedescendant={highlight >= 0 ? `med-picker-opt-${highlight}` : undefined}
                placeholder="Type name, generic, or code"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
              />
            </div>
          </div>
          <div ref={listRef} id="med-picker-list" className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                {query.trim() ? `No matches for "${query.trim()}".` : 'No medicines found.'}
                {!canAddNew && ' Add the medicine in the Medicines list first.'}
              </div>
            ) : filtered.map((m, i) => (
              <button
                key={m.id}
                type="button"
                tabIndex={-1}
                data-opt={i}
                id={`med-picker-opt-${i}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pickMedicine(m)}
                className={`w-full text-left px-3 py-2 border-b border-gray-50 last:border-0 transition-colors ${
                  highlight === i ? 'bg-emerald-100 shadow-[inset_4px_0_0_0_rgb(var(--c-emerald-600))]' : m.id === value ? 'bg-emerald-50' : 'hover:bg-emerald-50'
                }`}
              >
                <div className={`text-sm font-medium ${m.id === value ? 'text-emerald-700' : 'text-gray-900'}`}>{m.name}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  {m.genericName && <span>{m.genericName}</span>}
                  {m.genericName && <span>·</span>}
                  {m.medicineType && <span>{typeLabel(m.medicineType)}</span>}
                  {m.medicineType && <span>·</span>}
                  <span>Stock: {m.quantity ?? 0}</span>
                </div>
              </button>
            ))}
            {/* Inline "register a new medicine" — opens the Register Medicine form prefilled with
                the typed name, so a medicine that is not in the catalog yet can be created and used
                without leaving the receipt. Only shown to users who can create medicines. */}
            {canAddNew && onAddNew && (
              <button
                type="button"
                tabIndex={-1}
                data-opt={filtered.length}
                id={`med-picker-opt-${filtered.length}`}
                onMouseEnter={() => setHighlight(filtered.length)}
                onClick={registerNew}
                className={`w-full text-left px-3 py-2.5 border-t border-gray-100 text-emerald-700 flex items-center gap-2 text-sm font-medium ${
                  highlight === filtered.length ? 'bg-emerald-100 shadow-[inset_4px_0_0_0_rgb(var(--c-emerald-600))]' : 'hover:bg-emerald-50'
                }`}
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span>{query.trim() ? `Register "${query.trim()}" as a new medicine` : 'Register a new medicine'}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================== VIEW MODAL ===============================

// ReceiptRow — icon-led label/value row, same shape as VendorRow/AssetRow/UserRow.
const ReceiptRow = ({ icon, label, value, mono = false, highlight = false }) => {
  const isEmpty = value == null || value === '';
  return (
    <div className={`flex items-center justify-between gap-4 p-3.5 rounded-xl transition-all duration-200 hover:shadow-sm ${
      highlight ? 'bg-white bg-opacity-80 shadow-sm' : 'bg-white bg-opacity-40'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0">{icon}</div>
        <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{label}</span>
      </div>
      <span className={`font-semibold text-sm text-right break-words ${mono ? 'font-mono' : ''} ${
        isEmpty ? 'text-gray-400 italic font-normal' : 'text-gray-900'
      }`}>
        {isEmpty ? 'Not provided' : value}
      </span>
    </div>
  );
};

const ReceiptViewModal = ({ receipt, onClose, canEdit, onEdit }) => {
  const st = STATUS_STYLE[receipt.status] || STATUS_STYLE.ACTIVE;
  const formatDateLong = (d) => formatClinicDate(d, { day: 'numeric', month: 'long', year: 'numeric' }, null);

  const receiptViewDialogRef = useDialog(onClose, { confirmDiscard: false });

  return (
    <div ref={receiptViewDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] flex flex-col overflow-hidden">

        {/* Sticky emerald header. */}
        <div className="flex-shrink-0 relative bg-gradient-to-r from-navy-800 to-navy-700 text-white overflow-hidden">
          <button aria-label="Close" onClick={onClose} className="absolute top-4 right-4 z-10 p-2 hover:bg-white/20 rounded-xl transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="relative p-6">
            <div className="flex items-center space-x-5">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center border-2 border-white border-opacity-30 flex-shrink-0">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-3xl font-bold text-white">
                  {receipt.invoiceNumber}
                </h2>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 mt-1.5">
                  <span className="inline-flex items-center gap-1.5 text-sm text-white text-opacity-90">
                    <Truck className="h-4 w-4 opacity-80" />
                    {receipt.vendorName}
                  </span>
                  {/* Type chip — Medicine (emerald) vs Asset (blue). Mirrors the table. */}
                  {(receipt.receiptType || 'MEDICINE') === 'ASSET' ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-white/40 shadow-sm bg-blue-100 text-blue-800">
                      <Box className="h-3 w-3" /> Asset Receipt
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-white/40 shadow-sm bg-emerald-100 text-emerald-800">
                      <Pill className="h-3 w-3" /> Medicine Receipt
                    </span>
                  )}
                  {/* Status chip — Completed (warm amber) vs Cancelled (rose). Lives in
                      the header so the admin reads it the moment the modal opens. */}
                  {(() => {
                    const tone = receipt.status === 'CANCELLED'
                      ? 'bg-rose-200 border-rose-300 text-rose-900'
                      : 'bg-amber-200 border-amber-300 text-amber-900';
                    const dot = receipt.status === 'CANCELLED' ? 'bg-rose-600' : 'bg-amber-600';
                    return (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border shadow-sm ${tone}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                        {st.label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Summary card — invoice date, total, line count. Total is the headline so
              it gets the highlight treatment. */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-11 h-11 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">
                Receipt Summary
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ReceiptRow icon={<Calendar className="h-5 w-5 text-emerald-600" />} label="Invoice Date" value={formatDateLong(receipt.invoiceDate)} />
              <ReceiptRow icon={<Banknote className="h-5 w-5 text-emerald-700" />} label="Total" value={formatCurrency(receipt.totalAmount)} mono highlight />
              <ReceiptRow icon={<ListChecks className="h-5 w-5 text-teal-600" />} label="Lines" value={receipt.lineCount} mono />
              <ReceiptRow
                icon={<Banknote className="h-5 w-5 text-emerald-600" />}
                label="Payment"
                value={receipt.paymentMode
                  ? `${PAYMENT_MODE_LABEL[receipt.paymentMode] || receipt.paymentMode}${receipt.paymentReference ? ` · ${receipt.paymentReference}` : ''}`
                  : 'Not recorded'}
              />
              {receipt.orderNumber && (
                <ReceiptRow icon={<Truck className="h-5 w-5 text-navy-600" />} label="Against order" value={receipt.orderNumber} mono />
              )}
            </div>
          </div>

          {/* Cancellation reason banner — only when cancelled. Sits at the top so the
              admin sees the reason immediately. */}
          {receipt.status === 'CANCELLED' && receipt.cancellationReason && (
            <div className="bg-rose-50 border-2 border-rose-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <Ban className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-rose-800 mb-1">Cancellation Reason</div>
                  <p className="text-sm text-rose-900 whitespace-pre-wrap break-words">{receipt.cancellationReason}</p>
                </div>
              </div>
            </div>
          )}

          {/* Notes — optional free text recorded with the receipt. */}
          {receipt.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-amber-700 mb-1">Notes</div>
              <p className="text-sm text-amber-900 whitespace-pre-wrap break-words">{receipt.notes}</p>
            </div>
          )}

          {/* Line items — the table is the meat of the modal, given its own card-style
              wrapper to match the rest of the body. */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <ListChecks className="h-5 w-5 text-gray-500" />
              <h3 className="text-base font-bold text-gray-800">
                Line Items
              </h3>
            </div>
            <div className="overflow-x-auto">
              {(receipt.receiptType || 'MEDICINE') === 'ASSET' ? (
                /* Asset-receipt lines — different columns than medicine receipts. */
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-xs font-semibold text-gray-600 uppercase">
                      <th className="px-4 py-3 text-left">Asset</th>
                      <th className="px-4 py-3 text-left">Category</th>
                      <th className="px-4 py-3 text-left">Brand / Model</th>
                      <th className="px-4 py-3 text-right">Qty</th>
                      <th className="px-4 py-3 text-right">Unit Cost</th>
                      <th className="px-4 py-3 text-right">Warranty</th>
                      <th className="px-4 py-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(receipt.lines || []).map(l => (
                      <tr key={l.id} className="hover:bg-gray-50/50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{l.assetName || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {l.assetCategory ? l.assetCategory.replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase()) : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {l.assetBrand || l.assetModel
                            ? `${l.assetBrand || ''}${l.assetBrand && l.assetModel ? ' · ' : ''}${l.assetModel || ''}`
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">{l.quantityReceived}</td>
                        <td className="px-4 py-3 text-right font-mono">{formatCurrency(l.unitPurchasePrice)}</td>
                        <td className="px-4 py-3 text-right text-gray-700">
                          {l.assetWarrantyMonths ? `${l.assetWarrantyMonths} mo` : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-700">{formatCurrency(l.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm table-fixed">
                  <colgroup>
                    <col className="w-[21.5%]" />
                    <col className="w-[7.5%]" />
                    <col className="w-[9.5%]" />
                    <col className="w-[8%]" />
                    <col className="w-[10.5%]" />
                    <col className="w-[6.5%]" />
                    <col className="w-[6.5%]" />
                    <col className="w-[9.5%]" />
                    <col className="w-[9.5%]" />
                    <col className="w-[11%]" />
                  </colgroup>
                  {/* Same columns, same grouping and same glyphs as the entry form, so a saved
                      receipt can be read straight against the supplier's paper invoice. */}
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      <th colSpan={3} className="px-3 pt-2.5 pb-1 text-left">Item</th>
                      <th colSpan={4} className="px-3 pt-2.5 pb-1 text-center border-l border-gray-200">As billed by supplier</th>
                      <th colSpan={2} className="px-3 pt-2.5 pb-1 text-center border-l border-gray-200">Per unit</th>
                      <th className="px-3 pt-2.5 pb-1 border-l border-gray-200"></th>
                    </tr>
                    <tr className="text-[11px] font-semibold text-gray-600 uppercase">
                      <th className="px-3 pb-2.5 text-left">Medicine</th>
                      <th className="px-3 pb-2.5 text-left">Batch</th>
                      <th className="px-3 pb-2.5 text-left">Expiry</th>
                      <th className="px-3 pb-2.5 text-right border-l border-gray-200">Qty</th>
                      <th className="px-3 pb-2.5 text-right" title="This line's amount as the supplier's bill printed it, before discount and tax">
                        Gross Amount
                      </th>
                      <th className="px-3 pb-2.5 text-right" title="Trade discount taken off this line's gross before tax">
                        Disc %
                      </th>
                      <th className="px-3 pb-2.5 text-right" title="Sales tax charged on this line, after the discount">
                        Tax %
                      </th>
                      <th className="px-3 pb-2.5 text-right text-emerald-700 border-l border-gray-200" title="What a unit cost once this line's discount and tax were settled">Purchase</th>
                      <th className="px-3 pb-2.5 text-right" title="Patient-facing price per unit recorded on this receipt">Selling</th>
                      <th className="px-3 pb-2.5 text-right border-l border-gray-200" title="Net payable for this line, matching the supplier's invoice">
                        Line Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(receipt.lines || []).map(l => (
                      <tr key={l.id} className="hover:bg-gray-50/50">
                        <td className="px-3 py-3">
                          <div className="font-semibold text-gray-900">{l.medicineName}</div>
                          <div className="text-xs text-gray-500">{l.medicineGenericName} · {l.medicineCode}</div>
                        </td>
                        <td className="px-3 py-3 font-mono">{l.batchNumber || '-'}</td>
                        <td className="px-3 py-3">{formatDate(l.expiryDate)}</td>
                        <td className="px-3 py-3 text-right font-semibold border-l border-gray-100">
                          {l.quantityReceived}
                          {/* How the units were counted when the delivery came in. */}
                          {l.packsReceived != null && (
                            <div className="text-[10px] font-normal text-gray-500">
                              {l.packsReceived} {packEntryNoun(l.medicineType, l.packsReceived)} of{' '}
                              {l.unitsPerPackSnapshot || 1}
                            </div>
                          )}
                        </td>
                        {/* Receipts saved before per-line discount and tax existed carry neither a
                            list price nor the percentages. Back then the unit price WAS the rate,
                            so show it, and leave the percentages blank rather than claiming zero. */}
                        <td className="px-3 py-3 text-right font-mono">
                          {formatCurrency(l.listUnitPrice != null ? l.listUnitPrice : l.unitPurchasePrice)}
                          {l.packsReceived != null && (
                            <div className="text-[10px] font-normal text-gray-500">
                              {formatCurrency(l.packPurchasePrice)}/pack
                            </div>
                          )}
                        </td>
                        <td className={`px-3 py-3 text-right font-mono ${Number(l.discountPercent) > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                          {l.discountPercent != null ? formatPercent(l.discountPercent) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className={`px-3 py-3 text-right font-mono ${Number(l.taxPercent) > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                          {l.taxPercent != null ? formatPercent(l.taxPercent) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-emerald-700 border-l border-gray-100">{formatCurrency(l.unitPurchasePrice)}</td>
                        <td className="px-3 py-3 text-right font-mono">{l.unitSellingPrice != null ? formatCurrency(l.unitSellingPrice) : <span className="text-gray-300">—</span>}</td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-emerald-700 border-l border-gray-100">{formatCurrency(l.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* The supplier's bill. A receipt cannot exist without one, so this card is the whole
              point of opening the receipt: it is what the typed figures are checked against once
              everyone has forgotten the delivery. It was only ever shown on the edit form, so the
              only way to look at a bill was to open the receipt for editing, which is exactly the
              thing nobody should have to do to read something. */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
              <Paperclip className="h-5 w-5 text-gray-500" />
              <h3 className="text-base font-bold text-gray-800">Supplier Bill</h3>
              {(receipt.bills || []).length > 1 && (
                <span className="text-xs text-gray-500">{receipt.bills.length} copies</span>
              )}
            </div>
            {(receipt.bills || []).length === 0 ? (
              <p className="px-5 py-4 text-sm text-gray-500">
                No copy is attached. Every receipt recorded since bills became mandatory has one, so
                this receipt predates that.
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {receipt.bills.map(b => (
                  /* Hovering the row shows the bill itself, large enough to read, and moving away
                     closes it. Checking a figure against the paper is the whole reason for opening
                     a receipt, and a new tab for every glance is a poor way to do that. The URL
                     ends in /file rather than an extension, so the type comes from the record. */
                  <FileHoverPreview
                    key={b.id}
                    url={stockReceiptAPI.billFileUrl(b.id)}
                    label={b.originalFileName || 'Bill copy'}
                    isPdf={(b.contentType || '').toLowerCase().includes('pdf')}
                    className="block"
                    // A distributor's invoice is a tall page of small print. At the default size
                    // its line items cannot be read, which defeats the point of previewing it
                    // against the figures on screen.
                    maxWidth={680}
                    maxHeight={880}
                  >
                    {({ href }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className={`flex items-center gap-3 px-5 py-3 hover:bg-emerald-50 focus:bg-emerald-50 ${ROW_FOCUS_RING}`}
                        title="Hover to preview, click to open in a new tab"
                      >
                        <Paperclip className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-sm font-medium text-emerald-700">
                          {b.originalFileName || 'Bill copy'}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(b.sizeBytes)}</span>
                        <Eye className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </a>
                    )}
                  </FileHoverPreview>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer. Edit sits here because a figure that looks wrong is noticed while reading the
            receipt, and closing the dialog to hunt for the row again is the wrong answer. Gated
            exactly as the row menu gates it: active, permitted, and not an asset receipt, which
            the service refuses to edit because it would mutate auto-created assets. */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          {canEdit && (
            <button
              onClick={() => onEdit(receipt)}
              className="px-6 py-2.5 bg-white text-emerald-700 border border-emerald-200 rounded-xl font-semibold hover:bg-emerald-50 transition-colors shadow-sm inline-flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" /> Edit
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockReceiptsManagement;
